/**
 * 积木编程模块 - 完全独立的可视化编程环境
 * 使用 Google Blockly 库实现拖拽式编程
 * 数据与其他模块完全隔离
 */

// Blockly 全局类型声明
declare const Blockly: any;

// ========================================
// 独立数据存储 Key（与其他模块隔离）
// ========================================
const STORAGE_KEYS = {
  PROJECTS: 'codeverse_blocks_projects',
  CURRENT_PROJECT: 'codeverse_blocks_current',
  SETTINGS: 'codeverse_blocks_settings',
};

// ========================================
// 类型定义
// ========================================
interface Sprite {
  id: string;
  name: string;
  icon: string;
  x: number;
  y: number;
  direction: number;
  visible: boolean;
  workspace?: string; // Blockly XML
}

interface BlocksProject {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  sprites: Sprite[];
}

interface BlocksSettings {
  gridEnabled: boolean;
  rulerEnabled: boolean;
  soundEnabled: boolean;
  codeLanguage: 'javascript' | 'python';
}

// 积木分类定义
interface BlockCategory {
  id: string;
  name: string;
  icon: string;
  colour: string;
  blocks: { type: string; values?: any }[];
  custom?: string; // VARIABLE 或 PROCEDURE
}

// 积木分类配置
const BLOCK_CATEGORIES: BlockCategory[] = [
  {
    id: 'motion',
    name: '运动',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7"/></svg>',
    colour: '#4C97FF',
    blocks: [
      { type: 'motion_move' },
      { type: 'motion_turn_right' },
      { type: 'motion_turn_left' },
      { type: 'motion_goto_xy' },
      { type: 'motion_goto_random' },
      { type: 'motion_set_x' },
      { type: 'motion_set_y' },
      { type: 'motion_change_x' },
      { type: 'motion_change_y' },
    ]
  },
  {
    id: 'looks',
    name: '外观',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>',
    colour: '#9966FF',
    blocks: [
      { type: 'text_print' },
      { type: 'text' },
      { type: 'text_join' },
      { type: 'text_length' },
      { type: 'text_isEmpty' },
      { type: 'text_indexOf' },
      { type: 'text_charAt' },
      { type: 'text_getSubstring' },
      { type: 'text_changeCase' },
      { type: 'text_trim' },
      { type: 'looks_say' },
      { type: 'looks_show' },
      { type: 'looks_hide' },
    ]
  },
  {
    id: 'sound',
    name: '声音',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>',
    colour: '#CF63CF',
    blocks: [
      { type: 'sound_play' },
      { type: 'sound_stop' },
      { type: 'sound_set_volume' },
    ]
  },
  {
    id: 'events',
    name: '事件',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg>',
    colour: '#FFBF00',
    blocks: [
      { type: 'event_start' },
      { type: 'event_stage_clicked' },
    ]
  },
  {
    id: 'sensing',
    name: '检测',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>',
    colour: '#5CB1D6',
    blocks: [
      { type: 'sensing_key_pressed' },
      { type: 'sensing_timer' },
      { type: 'sensing_reset_timer' },
    ]
  },
  {
    id: 'control',
    name: '控制',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>',
    colour: '#FFAB19',
    blocks: [
      { type: 'controls_if' },
      { type: 'controls_repeat_ext' },
      { type: 'controls_repeat' },
      { type: 'controls_whileUntil' },
      { type: 'controls_for' },
      { type: 'controls_forEach' },
      { type: 'controls_flow_statements' },
      { type: 'wait_seconds' },
      { type: 'wait_until' },
    ]
  },
  {
    id: 'operators',
    name: '运算',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>',
    colour: '#59C059',
    blocks: [
      { type: 'math_number' },
      { type: 'math_arithmetic' },
      { type: 'math_single' },
      { type: 'math_trig' },
      { type: 'math_constant' },
      { type: 'math_number_property' },
      { type: 'math_round' },
      { type: 'math_on_list' },
      { type: 'math_modulo' },
      { type: 'math_constrain' },
      { type: 'math_random_int' },
      { type: 'math_random_float' },
      { type: 'math_atan2' },
      { type: 'logic_compare' },
      { type: 'logic_operation' },
      { type: 'logic_negate' },
      { type: 'logic_boolean' },
      { type: 'logic_null' },
      { type: 'logic_ternary' },
    ]
  },
  {
    id: 'variables',
    name: '变量',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 0 1 3 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18.21 0 .41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L6.04 7.5 12 10.85l5.96-3.35L12 4.15zM5 15.91l6 3.38v-6.71L5 9.21v6.7zm14 0v-6.7l-6 3.37v6.71l6-3.38z"/></svg>',
    colour: '#FF8C1A',
    blocks: [],
    custom: 'VARIABLE'
  },
  {
    id: 'lists',
    name: '列表',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/></svg>',
    colour: '#FF661A',
    blocks: [
      { type: 'lists_create_empty' },
      { type: 'lists_create_with' },
      { type: 'lists_repeat' },
      { type: 'lists_length' },
      { type: 'lists_isEmpty' },
      { type: 'lists_indexOf' },
      { type: 'lists_getIndex' },
      { type: 'lists_setIndex' },
      { type: 'lists_getSublist' },
      { type: 'lists_split' },
      { type: 'lists_sort' },
      { type: 'lists_reverse' },
    ]
  },
  {
    id: 'procedures',
    name: '函数',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>',
    colour: '#FF6680',
    blocks: [],
    custom: 'PROCEDURE'
  },
  {
    id: 'pen',
    name: '画笔',
    icon: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>',
    colour: '#0fBD8C',
    blocks: [
      { type: 'pen_down' },
      { type: 'pen_up' },
      { type: 'pen_clear' },
      { type: 'pen_move' },
      { type: 'pen_goto' },
      { type: 'pen_turn_right' },
      { type: 'pen_turn_left' },
      { type: 'pen_set_color' },
      { type: 'pen_set_size' },
      { type: 'pen_draw_circle' },
      { type: 'pen_draw_rect' },
      { type: 'pen_fill' },
    ]
  }
];

// 预设案例数据
// 导入预设案例数据
import { BLOCK_PRESETS, BlocksPreset } from '../../data/presets';

// 舞台类型
type StageType = 'pen' | 'sprite' | 'text';

// ========================================
// 积木编程模块主类
// ========================================
export class BlocksModule {
  private static instance: BlocksModule | null = null;
  
  /**
   * 隐藏舞台状态栏信息（在切换到其他模块时调用）
   */
  public static hideStageStatus(): void {
    const blocksStatusEl = document.getElementById('status-blocks');
    if (blocksStatusEl) {
      blocksStatusEl.style.display = 'none';
    }
  }
  
  private workspace: any = null;
  private currentProject: BlocksProject | null = null;
  private currentSprite: Sprite | null = null;
  private currentCategory: string = ''; // 当前选中的分类（空表示未选中）
  private lastCreatedVarName: string = ''; // 最后创建的变量名
    private selectedBlock: any = null; // 当前选中的积木块（用于删除功能）
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private codePreviewEditor: any = null; // CodeMirror 代码预览实例
  
  // 画布尺寸常量
  private readonly CANVAS_WIDTH = 480;
  private readonly CANVAS_HEIGHT = 360;
  private isRunning: boolean = false;
  private shouldStop: boolean = false; // 停止执行标志
  private isPaused: boolean = false; // 暂停执行标志
  private pauseResolve: (() => void) | null = null; // 暂停恢复回调
  private animationId: number | null = null;
  private sprites: Map<string, Sprite> = new Map();
  private outputLines: string[] = [];  // 输出行列表
  private stageType: StageType = 'pen';  // 舞台类型：画笔/角色
  private executionSpeed: number = 0; // 执行速度：0=快速（无延时）, 1=正常(200ms), 2=慢速(500ms)
  
  // 画布视图状态（拖动和缩放）
  private canvasView = {
    scale: 1,
    translateX: 0,
    translateY: 0,
    isDragging: false,
    lastX: 0,
    lastY: 0
  };
  
  // 画笔状态
  private penState = {
    isDown: false,
    x: 240,  // 画布中心 (CANVAS_WIDTH/2)
    y: 180,  // 画布中心 (CANVAS_HEIGHT/2)
    direction: 0,  // 向右为0度
    color: '#00ff00',
    size: 2
  };
  
  // 用户选择的绘制起点（null表示使用默认中心点）
  private userStartPoint: { x: number; y: number } | null = null;
  
  // 是否处于选择起点模式
  private isSelectingStartPoint: boolean = false;
  
  // 默认设置
  private settings: BlocksSettings = {
    gridEnabled: true,
    rulerEnabled: true,
    soundEnabled: true,
    codeLanguage: 'javascript'
  };
  
  // 筛选状态
  private filterState = {
    difficulty: 'all',  // 难度筛选: all, easy, medium, hard
    category: 'all'     // 类型筛选: all, 输出, 循环, 变量, 条件, 算法, 画笔
  };

  constructor() {
    console.log('🧱 积木编程模块初始化');
  }

  /**
   * 初始化模块
   */
  init(): void {
    // 加载设置
    this.loadSettings();
    
    // 应用设置模块中的积木器配置
    this.applyBlocksSettings();
    
    // 初始化画布
    this.initCanvas();
    
    // 初始化 Blockly 工作区
    this.initBlockly();
    
    // 创建默认项目
    this.createDefaultProject();
    
    // 绑定事件
    this.bindEvents();
    
    // 绑定弹窗事件
    this.bindModalEvents();
    this.bindAddSpriteModalEvents();
    
    // 绑定TAB切换事件
    this.bindTabEvents();
    
    // 渲染角色列表
    this.renderSpritesList();
    
    // 渲染积木分类列表
    this.renderCategoryList();
    
    // 渲染预设案例
    this.renderPresets();
    
    // 绑定筛选事件
    this.bindFilterEvents();
    
    // 应用初始舞台类型到UI
    this.setStageType(this.stageType);
    
    // 应用初始执行速度到UI
    this.updateSpeedButtonsUI();
    
    // 绑定小屏竖向TAB事件
    this.bindVerticalTabEvents();
    
    console.log('🧱 积木编程模块初始化完成');
  }

  /**
   * 刷新模块（切换到此模块时调用）
   */
  refresh(): void {
    // 重新调整 Blockly 工作区大小
    if (this.workspace) {
      Blockly.svgResize(this.workspace);
    }
    // 更新代码预览
    this.updateCodePreview();
    
    // 重新初始化TAB指示器位置（解决页面初始隐藏时无法获取尺寸的问题）
    this.updateTabIndicator();
    
    // 更新底部状态栏舞台信息
    this.updateStatusBar();
  }

  /**
   * 更新TAB指示器位置
   */
  private updateTabIndicator(): void {
    const tabsContainer = document.querySelector('.blocks-tabs');
    if (!tabsContainer) return;
    
    const indicator = tabsContainer.querySelector('.blocks-tab-indicator') as HTMLElement;
    const activeTab = tabsContainer.querySelector('.blocks-tab.active') as HTMLElement;
    
    if (indicator && activeTab && activeTab.offsetWidth > 0) {
      indicator.style.width = `${activeTab.offsetWidth}px`;
      indicator.style.left = `${activeTab.offsetLeft}px`;
    }
  }

  /**
   * 初始化 Canvas 画布
   */
  private initCanvas(): void {
    this.canvas = document.getElementById('blocks-canvas') as HTMLCanvasElement;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
      this.clearCanvas();
    }
    
    // 初始化画布拖动和缩放
    this.initCanvasDrag();
    
    // 初始化画布点击选择起点功能
    this.initCanvasClickToSetStart();
    
    // 初始化状态栏
    this.updateStatusBar();
    
    // 使用 ResizeObserver 监听容器尺寸变化，确保刻度尺正确渲染
    const container = document.getElementById('blocks-canvas-container');
    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        this.renderRulers();
      });
      resizeObserver.observe(container);
      
      // 初始渲染（使用 setTimeout 确保布局完成）
      setTimeout(() => this.renderRulers(), 100);
    }
  }
  
  /**
   * 初始化画布拖动和缩放功能
   */
  private initCanvasDrag(): void {
    const container = document.getElementById('blocks-canvas-container');
    const wrapper = document.getElementById('blocks-canvas-wrapper');
    if (!container || !wrapper) return;
    
    // 更新画布变换
    const updateTransform = () => {
      wrapper.style.transform = `translate(${this.canvasView.translateX}px, ${this.canvasView.translateY}px) scale(${this.canvasView.scale})`;
      this.renderRulers(); // 同步更新刻度尺
    };
    
    // 鼠标/触摸开始
    const onStart = (clientX: number, clientY: number) => {
      this.canvasView.isDragging = true;
      this.canvasView.lastX = clientX;
      this.canvasView.lastY = clientY;
      wrapper.classList.add('dragging');
    };
    
    // 鼠标/触摸移动
    const onMove = (clientX: number, clientY: number) => {
      if (!this.canvasView.isDragging) return;
      
      const deltaX = clientX - this.canvasView.lastX;
      const deltaY = clientY - this.canvasView.lastY;
      
      this.canvasView.translateX += deltaX;
      this.canvasView.translateY += deltaY;
      this.canvasView.lastX = clientX;
      this.canvasView.lastY = clientY;
      
      updateTransform();
    };
    
    // 鼠标/触摸结束
    const onEnd = () => {
      this.canvasView.isDragging = false;
      wrapper.classList.remove('dragging');
    };
    
    // 鼠标事件
    wrapper.addEventListener('mousedown', (e) => {
      e.preventDefault();
      onStart(e.clientX, e.clientY);
    });
    
    document.addEventListener('mousemove', (e) => {
      onMove(e.clientX, e.clientY);
    });
    
    document.addEventListener('mouseup', onEnd);
    
    // 触摸事件
    wrapper.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        onStart(touch.clientX, touch.clientY);
      }
    }, { passive: true });
    
    wrapper.addEventListener('touchmove', (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        onMove(touch.clientX, touch.clientY);
      }
    }, { passive: true });
    
    wrapper.addEventListener('touchend', onEnd);
    
    // 滚轮缩放
    container.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.canvasView.scale = Math.max(0.5, Math.min(3, this.canvasView.scale + delta));
      updateTransform();
    }, { passive: false });
    
    // 缩放按钮
    document.getElementById('canvas-zoom-in')?.addEventListener('click', () => {
      this.canvasView.scale = Math.min(3, this.canvasView.scale + 0.2);
      updateTransform();
    });
    
    document.getElementById('canvas-zoom-out')?.addEventListener('click', () => {
      this.canvasView.scale = Math.max(0.5, this.canvasView.scale - 0.2);
      updateTransform();
    });
    
    // 重置按钮
    document.getElementById('canvas-reset')?.addEventListener('click', () => {
      this.canvasView.scale = 1;
      this.canvasView.translateX = 0;
      this.canvasView.translateY = 0;
      updateTransform();
    });
  }
  
  /**
   * 初始化画布点击选择起点功能（按钮触发模式）
   */
  private initCanvasClickToSetStart(): void {
    if (!this.canvas) return;
    
    const container = document.getElementById('blocks-canvas-container');
    const setStartBtn = document.getElementById('canvas-set-start');
    const clearStartBtn = document.getElementById('canvas-clear-start');
    if (!container || !setStartBtn || !clearStartBtn) return;
    
    // 创建起点标记元素
    let startMarker = document.getElementById('canvas-start-marker');
    if (!startMarker) {
      startMarker = document.createElement('div');
      startMarker.id = 'canvas-start-marker';
      startMarker.className = 'canvas-start-marker';
      startMarker.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="8"/></svg>';
      startMarker.style.display = 'none';
      document.getElementById('blocks-canvas-wrapper')?.appendChild(startMarker);
    }
    
    // 创建选择提示元素
    let selectHint = document.getElementById('canvas-select-hint');
    if (!selectHint) {
      selectHint = document.createElement('div');
      selectHint.id = 'canvas-select-hint';
      selectHint.className = 'canvas-select-hint';
      selectHint.textContent = '点击画布选择起点';
      selectHint.style.display = 'none';
      container.appendChild(selectHint);
    }
    
    // 进入选择起点模式
    let hintTimer: number | null = null;
    const enterSelectMode = () => {
      if (this.stageType !== 'pen') {
        this.showCanvasToast('请先切换到画笔模式');
        return;
      }
      this.isSelectingStartPoint = true;
      container.classList.add('selecting-start');
      setStartBtn.classList.add('selecting');
      selectHint!.style.display = 'block';
      
      // 2秒后自动隐藏提示
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = window.setTimeout(() => {
        selectHint!.style.display = 'none';
      }, 2000);
    };
    
    // 退出选择起点模式
    const exitSelectMode = () => {
      this.isSelectingStartPoint = false;
      container.classList.remove('selecting-start');
      setStartBtn.classList.remove('selecting');
      selectHint!.style.display = 'none';
      if (hintTimer) {
        clearTimeout(hintTimer);
        hintTimer = null;
      }
    };
    
    // 设置起点按钮点击
    setStartBtn.addEventListener('click', () => {
      if (this.isSelectingStartPoint) {
        exitSelectMode();
      } else {
        enterSelectMode();
      }
    });
    
    // 清除起点按钮点击
    clearStartBtn.addEventListener('click', () => {
      this.userStartPoint = null;
      startMarker!.style.display = 'none';
      exitSelectMode();
      this.showCanvasToast('起点已重置为画布中心');
    });
    
    // 获取点击/触摸坐标
    const getCanvasCoords = (clientX: number, clientY: number): { x: number; y: number } => {
      const rect = this.canvas!.getBoundingClientRect();
      const scaleX = this.canvas!.width / rect.width;
      const scaleY = this.canvas!.height / rect.height;
      return {
        x: Math.round((clientX - rect.left) * scaleX),
        y: Math.round((clientY - rect.top) * scaleY)
      };
    };
    
    // 设置起点
    const setStartPoint = (x: number, y: number) => {
      // 限制在画布范围内
      x = this.clampX(x);
      y = this.clampY(y);
      
      this.userStartPoint = { x, y };
      
      // 显示起点标记
      const markerX = (x / this.CANVAS_WIDTH) * 100;
      const markerY = (y / this.CANVAS_HEIGHT) * 100;
      startMarker!.style.display = 'block';
      startMarker!.style.left = `${markerX}%`;
      startMarker!.style.top = `${markerY}%`;
      
      exitSelectMode();
      this.showCanvasToast(`起点已设置为 (${x}, ${y})`);
    };
    
    // 画布点击事件（鼠标）
    this.canvas.addEventListener('click', (e) => {
      if (!this.isSelectingStartPoint) return;
      e.preventDefault();
      e.stopPropagation();
      
      const coords = getCanvasCoords(e.clientX, e.clientY);
      setStartPoint(coords.x, coords.y);
    });
    
    // 画布触摸事件
    this.canvas.addEventListener('touchend', (e) => {
      if (!this.isSelectingStartPoint) return;
      e.preventDefault();
      
      if (e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        const coords = getCanvasCoords(touch.clientX, touch.clientY);
        setStartPoint(coords.x, coords.y);
      }
    });
    
    // 小屏标题栏按钮代理（触发原有按钮）
    document.getElementById('canvas-set-start-header')?.addEventListener('click', () => {
      setStartBtn.click();
    });
    document.getElementById('canvas-clear-start-header')?.addEventListener('click', () => {
      clearStartBtn.click();
    });
    document.getElementById('canvas-reset-header')?.addEventListener('click', () => {
      document.getElementById('canvas-reset')?.click();
    });
  }
  
  /**
   * 在画布区域显示简单提示
   */
  private showCanvasToast(message: string): void {
    const container = document.getElementById('blocks-canvas-container');
    if (!container) return;
    
    // 移除旧的提示
    const oldToast = container.querySelector('.canvas-toast');
    if (oldToast) oldToast.remove();
    
    // 创建新提示
    const toast = document.createElement('div');
    toast.className = 'canvas-toast';
    toast.textContent = message;
    container.appendChild(toast);
    
    // 2秒后自动消失
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  /**
   * 清空画布并绘制背景
   */
  private clearCanvas(): void {
    if (!this.ctx || !this.canvas) return;
    
    // 根据舞台类型设置背景
    if (this.stageType === 'pen') {
      // 画笔模式：深色背景
      this.ctx.fillStyle = '#1a1a2e';
    } else {
      // 角色模式：白色背景
      this.ctx.fillStyle = '#ffffff';
    }
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // 绘制网格（可选）
    if (this.settings.gridEnabled) {
      this.drawGrid();
    }
    
    // 角色模式下绘制角色
    if (this.stageType === 'sprite') {
      this.drawSprites();
    }
  }

  /**
   * 绘制网格和坐标轴（画布内部简化版）
   */
  private drawGrid(): void {
    if (!this.ctx || !this.canvas) return;
    
    const ctx = this.ctx;
    const w = this.canvas.width;
    const h = this.canvas.height;
    const centerX = w / 2;
    const centerY = h / 2;
    const gridSize = 40;
    
    // 根据舞台类型选择颜色
    const isPenMode = this.stageType === 'pen';
    const gridColor = isPenMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
    const axisColor = isPenMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';
    
    // 绘制网格线
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    
    // 垂直网格线
    for (let x = centerX % gridSize; x <= w; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    
    // 水平网格线
    for (let y = centerY % gridSize; y <= h; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    
    // 绘制坐标轴（简化，只画线）
    ctx.strokeStyle = axisColor;
    ctx.lineWidth = 1;
    
    // X轴
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(w, centerY);
    ctx.stroke();
    
    // Y轴
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, h);
    ctx.stroke();
    
    // 绘制原点标记
    ctx.fillStyle = isPenMode ? '#4ade80' : '#22c55e';
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  
  /**
   * 渲染外围刻度尺
   */
  private renderRulers(): void {
    const rulerX = document.getElementById('canvas-ruler-x');
    const rulerY = document.getElementById('canvas-ruler-y');
    const canvas = document.getElementById('blocks-canvas');
    if (!rulerX || !rulerY || !canvas) return;
    
    const w = this.CANVAS_WIDTH;   // 480
    const h = this.CANVAS_HEIGHT;  // 360
    const centerX = w / 2;  // 240
    const centerY = h / 2;  // 180
    const baseStep = 40;
    
    // 获取各元素的实际位置
    const canvasRect = canvas.getBoundingClientRect();
    const rulerXRect = rulerX.getBoundingClientRect();
    const rulerYRect = rulerY.getBoundingClientRect();
    
    // 缩放比例
    const ratioX = canvasRect.width / w;
    const ratioY = canvasRect.height / h;
    
    // 清空现有内容
    rulerX.innerHTML = '';
    rulerY.innerHTML = '';
    
    // X轴刻度（底部）
    for (let canvasX = -centerX; canvasX <= centerX; canvasX += baseStep) {
      // 该坐标在视口中的位置
      const posInViewport = canvasRect.left + (centerX + canvasX) * ratioX;
      // 相对于刻度尺的位置
      const posInRuler = posInViewport - rulerXRect.left;
      
      // 只显示在刻度尺可见范围内的刻度
      if (posInRuler >= 0 && posInRuler <= rulerXRect.width) {
        const tick = document.createElement('span');
        tick.className = 'ruler-tick' + (canvasX === 0 ? ' zero' : '');
        tick.textContent = String(canvasX);
        tick.style.left = `${posInRuler}px`;
        rulerX.appendChild(tick);
      }
    }
    
    // Y轴刻度（左侧）
    for (let canvasY = -centerY; canvasY <= centerY; canvasY += baseStep) {
      // 该坐标在视口中的位置（Y轴向上为正）
      const posInViewport = canvasRect.top + (centerY - canvasY) * ratioY;
      // 相对于刻度尺的位置
      const posInRuler = posInViewport - rulerYRect.top;
      
      if (posInRuler >= 0 && posInRuler <= rulerYRect.height) {
        const tick = document.createElement('span');
        tick.className = 'ruler-tick' + (canvasY === 0 ? ' zero' : '');
        tick.textContent = String(canvasY);
        tick.style.top = `${posInRuler}px`;
        rulerY.appendChild(tick);
      }
    }
  }

  /**
   * 绘制所有角色
   */
  private drawSprites(): void {
    if (!this.ctx || !this.canvas) return;
    
    this.sprites.forEach(sprite => {
      if (!sprite.visible) return;
      
      // 转换坐标（Scratch风格：中心为原点）
      const x = this.canvas!.width / 2 + sprite.x;
      const y = this.canvas!.height / 2 - sprite.y;
      
      // 绘制角色（使用 emoji 或图标）
      this.ctx!.save();
      this.ctx!.translate(x, y);
      this.ctx!.rotate((sprite.direction - 90) * Math.PI / 180);
      this.ctx!.font = '32px serif';
      this.ctx!.textAlign = 'center';
      this.ctx!.textBaseline = 'middle';
      this.ctx!.fillText(sprite.icon, 0, 0);
      this.ctx!.restore();
    });
  }
  
  /**
   * 更新角色显示（运动时调用）
   */
  private updateSpriteDisplay(state: { x: number; y: number; direction: number }): void {
    if (!this.ctx || !this.canvas) return;
    
    // 清空画布
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // 绘制网格
    if (this.settings.gridEnabled) {
      this.drawGrid();
    }
    
    // 将 Scratch 坐标转换为画布坐标
    // Scratch: 中心(0,0)，X向右正，Y向上正
    // Canvas: 左上角(0,0)，X向右正，Y向下正
    const canvasX = this.canvas.width / 2 + state.x;
    const canvasY = this.canvas.height / 2 - state.y;
    
    this.ctx.save();
    this.ctx.translate(canvasX, canvasY);
    this.ctx.rotate((state.direction - 90) * Math.PI / 180);
    
    // 使用当前角色的 emoji 图标绘制
    const icon = this.currentSprite?.icon || '🐱';
    this.ctx.font = '32px serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText(icon, 0, 0);
    
    this.ctx.restore();
  }

  /**
   * 初始化 Blockly 工作区
   */
  private initBlockly(): void {
    const container = document.getElementById('blocks-workspace');
    if (!container || typeof Blockly === 'undefined') {
      console.warn('Blockly 未加载或容器不存在');
      return;
    }

    // 注册自定义画笔积木
    this.registerPenBlocks();

    // 注入 Blockly（使用 flyoutToolbox 模式，初始为空）
    this.workspace = Blockly.inject(container, {
      media: 'https://unpkg.com/blockly/media/',  // Blockly 媒体资源路径
      toolbox: {
        kind: 'flyoutToolbox',
        contents: [] // 初始为空，等待用户点击分类
      },
      grid: {
        spacing: 20,
        length: 3,
        colour: '#ccc',
        snap: true
      },
      zoom: {
        controls: false,  // 禁用默认控件，使用自定义控件
        wheel: true,
        startScale: 1.0,
        maxScale: 2,
        minScale: 0.5,
        scaleSpeed: 1.2
      },
      trashcan: false,  // 禁用默认垃圾桶，使用自定义控件
      move: {
        scrollbars: true,
        drag: true,
        wheel: true
      },
      sounds: this.settings.soundEnabled,
      theme: this.createTheme()
    });

    // 监听工作区变化
    this.workspace.addChangeListener((event: any) => {
      this.updateCodePreview();
      this.saveCurrentSpriteWorkspace();
      
      // 监听选择变化，保存选中的积木块
      if (event.type === 'selected') {
        if (event.newElementId) {
          this.selectedBlock = this.workspace.getBlockById(event.newElementId);
        } else {
          this.selectedBlock = null;
        }
      }
    });
    
    // 自定义代码生成器：让 text_print 使用 output 函数
    this.registerCustomGenerators();
    
    // 初始时隐藏 Flyout
    container.classList.add('flyout-hidden');
  }

  /**
   * 注册自定义代码生成器
   */
  private registerCustomGenerators(): void {
    const jsGenerator = (Blockly as any).JavaScript;
    if (!jsGenerator) return;
    
    // 设置语句前缀，用于高亮和延时
    jsGenerator.STATEMENT_PREFIX = 'await highlightBlock(%1);\n';
    jsGenerator.addReservedWords('highlightBlock');
    
    // 重写 text_print：使用 output 函数而非 window.alert
    jsGenerator.forBlock['text_print'] = function(block: any, generator: any) {
      const msg = generator.valueToCode(block, 'TEXT', generator.ORDER_NONE) || "''";
      return `output(${msg});\n`;
    };
    
    // 重写变量初始化：确保变量有默认值 0
    const originalVarsDef = jsGenerator.init;
    jsGenerator.init = function(workspace: any) {
      originalVarsDef.call(this, workspace);
          // 初始化所有变量为 0
      const variables = workspace.getAllVariables();
      if (variables && variables.length > 0 && jsGenerator.nameDB_) {
        // 安全检查 setVariablePrefix 方法是否存在
        if (typeof jsGenerator.nameDB_.setVariablePrefix === 'function') {
          jsGenerator.nameDB_.setVariablePrefix('');
        }
        variables.forEach((v: any) => {
          const varName = jsGenerator.nameDB_.getName(v.name, (Blockly as any).Names.NameType.VARIABLE);
          if (!jsGenerator.definitions_['variables_' + varName]) {
            jsGenerator.definitions_['variables_' + varName] = `var ${varName} = 0;`;
          }
        });
      }
    };
    
    // 画笔积木代码生成器
    jsGenerator.forBlock['pen_down'] = () => 'penDown();\n';
    jsGenerator.forBlock['pen_up'] = () => 'penUp();\n';
    jsGenerator.forBlock['pen_clear'] = () => 'penClear();\n';
    jsGenerator.forBlock['pen_move'] = (block: any, generator: any) => {
      const steps = generator.valueToCode(block, 'STEPS', generator.ORDER_NONE) || '10';
      return `penMove(${steps});\n`;
    };
    jsGenerator.forBlock['pen_goto'] = (block: any, generator: any) => {
      const x = generator.valueToCode(block, 'X', generator.ORDER_NONE) || '0';
      const y = generator.valueToCode(block, 'Y', generator.ORDER_NONE) || '0';
      return `penGoto(${x}, ${y});\n`;
    };
    jsGenerator.forBlock['pen_turn_right'] = (block: any, generator: any) => {
      const angle = generator.valueToCode(block, 'ANGLE', generator.ORDER_NONE) || '90';
      return `penTurnRight(${angle});\n`;
    };
    jsGenerator.forBlock['pen_turn_left'] = (block: any, generator: any) => {
      const angle = generator.valueToCode(block, 'ANGLE', generator.ORDER_NONE) || '90';
      return `penTurnLeft(${angle});\n`;
    };
    jsGenerator.forBlock['pen_set_color'] = (block: any) => {
      const color = block.getFieldValue('COLOR') || '#00ff00';
      return `penSetColor('${color}');\n`;
    };
    jsGenerator.forBlock['pen_set_size'] = (block: any, generator: any) => {
      const size = generator.valueToCode(block, 'SIZE', generator.ORDER_NONE) || '2';
      return `penSetSize(${size});\n`;
    };
    
    jsGenerator.forBlock['pen_draw_circle'] = (block: any, generator: any) => {
      const radius = generator.valueToCode(block, 'RADIUS', generator.ORDER_NONE) || '50';
      return `penDrawCircle(${radius});\n`;
    };
    
    jsGenerator.forBlock['pen_draw_rect'] = (block: any, generator: any) => {
      const width = generator.valueToCode(block, 'WIDTH', generator.ORDER_NONE) || '100';
      const height = generator.valueToCode(block, 'HEIGHT', generator.ORDER_NONE) || '50';
      return `penDrawRect(${width}, ${height});\n`;
    };
    
    jsGenerator.forBlock['pen_fill'] = (block: any) => {
      const fill = block.getFieldValue('FILL');
      return `penSetFill(${fill});\n`;
    };
    
    // ========== 角色外观代码生成器 ==========
    jsGenerator.forBlock['looks_say'] = (block: any, generator: any) => {
      const text = generator.valueToCode(block, 'TEXT', generator.ORDER_NONE) || "''";
      const secs = generator.valueToCode(block, 'SECS', generator.ORDER_NONE) || '2';
      return `await spriteSay(${text}, ${secs});\n`;
    };
    
    jsGenerator.forBlock['looks_show'] = () => {
      return 'spriteShow();\n';
    };
    
    jsGenerator.forBlock['looks_hide'] = () => {
      return 'spriteHide();\n';
    };
    
    // ========== 声音代码生成器 ==========
    jsGenerator.forBlock['sound_play'] = (block: any) => {
      const sound = block.getFieldValue('SOUND');
      return `playSound('${sound}');\n`;
    };
    
    jsGenerator.forBlock['sound_stop'] = () => {
      return `stopAllSounds();\n`;
    };
    
    jsGenerator.forBlock['sound_set_volume'] = (block: any, generator: any) => {
      const volume = generator.valueToCode(block, 'VOLUME', generator.ORDER_NONE) || '100';
      return `setVolume(${volume});\n`;
    };
    
    // ========== 事件代码生成器 ==========
    jsGenerator.forBlock['event_start'] = () => {
      return '';  // 启动事件不生成代码，只是入口点
    };
    
    jsGenerator.forBlock['event_key_pressed'] = () => {
      return '';  // 事件入口点
    };
    
    jsGenerator.forBlock['event_stage_clicked'] = () => {
      return '';  // 事件入口点
    };
    
    // ========== 等待代码生成器 ==========
    jsGenerator.forBlock['wait_seconds'] = (block: any, generator: any) => {
      const seconds = generator.valueToCode(block, 'SECONDS', generator.ORDER_NONE) || '1';
      return `await waitSeconds(${seconds});\n`;
    };
    
    jsGenerator.forBlock['wait_until'] = (block: any, generator: any) => {
      const condition = generator.valueToCode(block, 'CONDITION', generator.ORDER_NONE) || 'false';
      return `await waitUntil(() => ${condition});\n`;
    };
    
    // ========== 检测代码生成器 ==========
    jsGenerator.forBlock['sensing_key_pressed'] = (block: any) => {
      const key = block.getFieldValue('KEY');
      return [`isKeyPressed('${key}')`, jsGenerator.ORDER_ATOMIC];
    };
    
    jsGenerator.forBlock['sensing_timer'] = () => {
      return ['getTimer()', jsGenerator.ORDER_ATOMIC];
    };
    
    jsGenerator.forBlock['sensing_reset_timer'] = () => {
      return 'resetTimer();\n';
    };
    
    // ========== 运动代码生成器 ==========
    jsGenerator.forBlock['motion_move'] = (block: any, generator: any) => {
      const steps = generator.valueToCode(block, 'STEPS', generator.ORDER_NONE) || '10';
      return `spriteMove(${steps});\n`;
    };
    
    jsGenerator.forBlock['motion_turn_right'] = (block: any, generator: any) => {
      const angle = generator.valueToCode(block, 'ANGLE', generator.ORDER_NONE) || '15';
      return `spriteTurnRight(${angle});\n`;
    };
    
    jsGenerator.forBlock['motion_turn_left'] = (block: any, generator: any) => {
      const angle = generator.valueToCode(block, 'ANGLE', generator.ORDER_NONE) || '15';
      return `spriteTurnLeft(${angle});\n`;
    };
    
    jsGenerator.forBlock['motion_goto_xy'] = (block: any, generator: any) => {
      const x = generator.valueToCode(block, 'X', generator.ORDER_NONE) || '0';
      const y = generator.valueToCode(block, 'Y', generator.ORDER_NONE) || '0';
      return `spriteGoto(${x}, ${y});\n`;
    };
    
    jsGenerator.forBlock['motion_goto_random'] = () => {
      return `spriteGotoRandom();\n`;
    };
    
    jsGenerator.forBlock['motion_set_x'] = (block: any, generator: any) => {
      const x = generator.valueToCode(block, 'X', generator.ORDER_NONE) || '0';
      return `spriteSetX(${x});\n`;
    };
    
    jsGenerator.forBlock['motion_set_y'] = (block: any, generator: any) => {
      const y = generator.valueToCode(block, 'Y', generator.ORDER_NONE) || '0';
      return `spriteSetY(${y});\n`;
    };
    
    jsGenerator.forBlock['motion_change_x'] = (block: any, generator: any) => {
      const dx = generator.valueToCode(block, 'DX', generator.ORDER_NONE) || '10';
      return `spriteChangeX(${dx});\n`;
    };
    
    jsGenerator.forBlock['motion_change_y'] = (block: any, generator: any) => {
      const dy = generator.valueToCode(block, 'DY', generator.ORDER_NONE) || '10';
      return `spriteChangeY(${dy});\n`;
    };
  }

  /**
   * 注册自定义画笔积木
   */
  private registerPenBlocks(): void {
    // 确保 Blockly.Blocks 存在
    if (typeof Blockly === 'undefined' || !Blockly.Blocks) {
      console.warn('画笔积木注册失败: Blockly.Blocks 不存在');
      return;
    }
    
    // 落笔
    Blockly.Blocks['pen_down'] = {
      init: function() {
        this.appendDummyInput().appendField('落笔');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('开始绘制，移动时留下轨迹');
      }
    };

    // 抬笔
    Blockly.Blocks['pen_up'] = {
      init: function() {
        this.appendDummyInput().appendField('抬笔');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('停止绘制，移动时不留轨迹');
      }
    };

    // 清空画布
    Blockly.Blocks['pen_clear'] = {
      init: function() {
        this.appendDummyInput().appendField('清空画布');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('擦除所有绘制内容');
      }
    };

    // 移动
    Blockly.Blocks['pen_move'] = {
      init: function() {
        this.appendValueInput('STEPS')
          .setCheck('Number')
          .appendField('移动');
        this.appendDummyInput().appendField('步');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('向当前方向移动指定步数');
      }
    };

    // 移到坐标
    Blockly.Blocks['pen_goto'] = {
      init: function() {
        this.appendDummyInput().appendField('移到 x:');
        this.appendValueInput('X').setCheck('Number');
        this.appendDummyInput().appendField('y:');
        this.appendValueInput('Y').setCheck('Number');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('移动到指定坐标');
      }
    };

    // 右转
    Blockly.Blocks['pen_turn_right'] = {
      init: function() {
        this.appendValueInput('ANGLE')
          .setCheck('Number')
          .appendField('右转');
        this.appendDummyInput().appendField('度');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('顺时针旋转指定角度');
      }
    };

    // 左转
    Blockly.Blocks['pen_turn_left'] = {
      init: function() {
        this.appendValueInput('ANGLE')
          .setCheck('Number')
          .appendField('左转');
        this.appendDummyInput().appendField('度');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('逆时针旋转指定角度');
      }
    };

    // 设置颜色
    Blockly.Blocks['pen_set_color'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('设置画笔颜色')
          .appendField(new Blockly.FieldDropdown([
            ['绿色', '#00ff00'],
            ['红色', '#ff0000'],
            ['蓝色', '#0000ff'],
            ['黄色', '#ffff00'],
            ['橙色', '#ff8800'],
            ['紫色', '#8800ff'],
            ['白色', '#ffffff'],
            ['黑色', '#000000']
          ]), 'COLOR');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('设置画笔的颜色');
      }
    };

    // 设置粗细
    Blockly.Blocks['pen_set_size'] = {
      init: function() {
        this.appendValueInput('SIZE')
          .setCheck('Number')
          .appendField('设置画笔粗细');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('设置画笔的线条宽度');
      }
    };
    
    Blockly.Blocks['pen_draw_circle'] = {
      init: function() {
        this.appendValueInput('RADIUS')
          .setCheck('Number')
          .appendField('画圆，半径');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('在当前位置画一个圆');
      }
    };
    
    Blockly.Blocks['pen_draw_rect'] = {
      init: function() {
        this.appendValueInput('WIDTH')
          .setCheck('Number')
          .appendField('画矩形，宽');
        this.appendValueInput('HEIGHT')
          .setCheck('Number')
          .appendField('高');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('在当前位置画一个矩形');
      }
    };
    
    Blockly.Blocks['pen_fill'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('填充模式')
          .appendField(new Blockly.FieldDropdown([
            ['开启', 'true'],
            ['关闭', 'false']
          ]), 'FILL');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#0fBD8C');
        this.setTooltip('开启或关闭填充模式');
      }
    };
    
    // ========== 角色外观积木 ==========
    Blockly.Blocks['looks_say'] = {
      init: function() {
        this.appendValueInput('TEXT')
          .appendField('说');
        this.appendValueInput('SECS')
          .setCheck('Number')
          .appendField('秒');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#9966FF');
        this.setTooltip('让角色显示说话气泡');
      }
    };
    
    Blockly.Blocks['looks_show'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('显示');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#9966FF');
        this.setTooltip('显示角色');
      }
    };
    
    Blockly.Blocks['looks_hide'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('隐藏');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#9966FF');
        this.setTooltip('隐藏角色');
      }
    };
    
    // ========== 声音积木 ==========
    Blockly.Blocks['sound_play'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('播放声音')
          .appendField(new Blockly.FieldDropdown([
            ['提示音', 'beep'],
            ['成功', 'success'],
            ['警告', 'warning'],
            ['错误', 'error'],
            ['点击', 'click']
          ]), 'SOUND');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#CF63CF');
        this.setTooltip('播放指定的声音效果');
      }
    };
    
    Blockly.Blocks['sound_stop'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('停止所有声音');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#CF63CF');
        this.setTooltip('停止所有正在播放的声音');
      }
    };
    
    Blockly.Blocks['sound_set_volume'] = {
      init: function() {
        this.appendValueInput('VOLUME')
          .setCheck('Number')
          .appendField('设置音量为');
        this.appendDummyInput()
          .appendField('%');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#CF63CF');
        this.setTooltip('设置音量大小(0-100)');
      }
    };
    
    // ========== 事件积木 ==========
    Blockly.Blocks['event_start'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('当程序启动时');
        this.setNextStatement(true, null);
        this.setColour('#FFBF00');
        this.setTooltip('程序开始运行时执行');
      }
    };
    
    Blockly.Blocks['event_key_pressed'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('当点击舞台时');
        this.setNextStatement(true, null);
        this.setColour('#FFBF00');
        this.setTooltip('当用户点击舞台时执行');
      }
    };
    
    Blockly.Blocks['event_stage_clicked'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('当点击舞台时');
        this.setNextStatement(true, null);
        this.setColour('#FFBF00');
        this.setTooltip('当用户点击舞台时执行');
      }
    };
    
    // ========== 等待积木 ==========
    Blockly.Blocks['wait_seconds'] = {
      init: function() {
        this.appendValueInput('SECONDS')
          .setCheck('Number')
          .appendField('等待');
        this.appendDummyInput()
          .appendField('秒');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#FFAB19');
        this.setTooltip('等待指定秒数后继续执行');
      }
    };
    
    Blockly.Blocks['wait_until'] = {
      init: function() {
        this.appendValueInput('CONDITION')
          .setCheck('Boolean')
          .appendField('等待直到');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#FFAB19');
        this.setTooltip('等待直到条件成立后继续执行');
      }
    };
    
    // ========== 检测积木 ==========
    Blockly.Blocks['sensing_key_pressed'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('按键')
          .appendField(new Blockly.FieldDropdown([
            ['空格', 'space'],
            ['上箭头', 'ArrowUp'],
            ['下箭头', 'ArrowDown'],
            ['左箭头', 'ArrowLeft'],
            ['右箭头', 'ArrowRight'],
            ['a', 'a'], ['b', 'b'], ['c', 'c'], ['d', 'd'],
            ['w', 'w'], ['s', 's'],
          ]), 'KEY')
          .appendField('按下?');
        this.setOutput(true, 'Boolean');
        this.setColour('#5CB1D6');
        this.setTooltip('检测指定按键是否按下');
      }
    };
    
    Blockly.Blocks['sensing_timer'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('计时器');
        this.setOutput(true, 'Number');
        this.setColour('#5CB1D6');
        this.setTooltip('获取程序运行的时间（秒）');
      }
    };
    
    Blockly.Blocks['sensing_reset_timer'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('重置计时器');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#5CB1D6');
        this.setTooltip('将计时器重置为 0');
      }
    };
    
    // ========== 运动积木 ==========
    Blockly.Blocks['motion_move'] = {
      init: function() {
        this.appendValueInput('STEPS')
          .setCheck('Number')
          .appendField('移动');
        this.appendDummyInput()
          .appendField('步');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('向当前方向移动指定步数');
      }
    };
    
    Blockly.Blocks['motion_turn_right'] = {
      init: function() {
        this.appendValueInput('ANGLE')
          .setCheck('Number')
          .appendField('右转');
        this.appendDummyInput()
          .appendField('度');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('向右旋转指定角度');
      }
    };
    
    Blockly.Blocks['motion_turn_left'] = {
      init: function() {
        this.appendValueInput('ANGLE')
          .setCheck('Number')
          .appendField('左转');
        this.appendDummyInput()
          .appendField('度');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('向左旋转指定角度');
      }
    };
    
    Blockly.Blocks['motion_goto_xy'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('移到 x:');
        this.appendValueInput('X')
          .setCheck('Number');
        this.appendDummyInput()
          .appendField('y:');
        this.appendValueInput('Y')
          .setCheck('Number');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('移动到指定坐标');
      }
    };
    
    Blockly.Blocks['motion_goto_random'] = {
      init: function() {
        this.appendDummyInput()
          .appendField('移到随机位置');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('移动到舞台上的随机位置');
      }
    };
    
    Blockly.Blocks['motion_set_x'] = {
      init: function() {
        this.appendValueInput('X')
          .setCheck('Number')
          .appendField('设 x 为');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('设置角色的x坐标');
      }
    };
    
    Blockly.Blocks['motion_set_y'] = {
      init: function() {
        this.appendValueInput('Y')
          .setCheck('Number')
          .appendField('设 y 为');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('设置角色的y坐标');
      }
    };
    
    Blockly.Blocks['motion_change_x'] = {
      init: function() {
        this.appendValueInput('DX')
          .setCheck('Number')
          .appendField('将 x 增加');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('增加角色的x坐标');
      }
    };
    
    Blockly.Blocks['motion_change_y'] = {
      init: function() {
        this.appendValueInput('DY')
          .setCheck('Number')
          .appendField('将 y 增加');
        this.setInputsInline(true);
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour('#4C97FF');
        this.setTooltip('增加角色的y坐标');
      }
    };
  }

  /**
   * 渲染积木分类列表
   */
  private renderCategoryList(): void {
    const container = document.getElementById('blocks-category-list');
    if (!container) return;

    container.innerHTML = BLOCK_CATEGORIES.map(cat => `
      <button 
        class="blocks-category-btn ${cat.id === this.currentCategory ? 'active' : ''}"
        data-category="${cat.id}"
        style="--cat-color: ${cat.colour}"
      >
        <span class="blocks-category-icon-wrapper" style="background: ${cat.colour}">
          ${cat.icon}
        </span>
        <span class="blocks-category-name">${cat.name}</span>
      </button>
    `).join('');

    // 绑定点击事件
    container.querySelectorAll('.blocks-category-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const categoryId = target.dataset.category;
        if (categoryId) {
          this.selectCategory(categoryId);
        }
      });
    });
    // 初始时不选中任何分类，Flyout 保持收起状态
  }

  /**
   * 选中分类（支持 toggle 收起）
   */
  private selectCategory(categoryId: string): void {
    const container = document.getElementById('blocks-category-list');
    
    // 如果点击的是当前已选中的分类，则收起 Flyout
    if (this.currentCategory === categoryId) {
      this.currentCategory = '';
      
      // 取消所有选中状态
      if (container) {
        container.querySelectorAll('.blocks-category-btn').forEach(btn => {
          btn.classList.remove('active');
        });
      }
      
      // 收起 Flyout
      this.hideFlyout();
      return;
    }
    
    // 否则选中新分类
    this.currentCategory = categoryId;
    
    // 更新按钮状态
    if (container) {
      container.querySelectorAll('.blocks-category-btn').forEach(btn => {
        btn.classList.toggle('active', (btn as HTMLElement).dataset.category === categoryId);
      });
    }

    // 显示对应积木
    this.showCategoryBlocks(categoryId);
  }

  /**
   * 收起/隐藏 Flyout
   */
  private hideFlyout(): void {
    if (!this.workspace) return;
    
    try {
      // 更新工具箱为空内容
      this.workspace.updateToolbox({
        kind: 'flyoutToolbox',
        contents: []
      });
      
      // 添加隐藏标记
      const container = document.getElementById('blocks-workspace');
      if (container) {
        container.classList.add('flyout-hidden');
      }
    } catch (e) {
      console.warn('收起 Flyout 失败:', e);
    }
  }

  /**
   * 绑定弹窗事件
   */
  private bindModalEvents(): void {
    // 取消按钮
    const cancelBtn = document.getElementById('blocks-variable-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.hideVariableModal());
    }
    
    // 确认按钮
    const confirmBtn = document.getElementById('blocks-variable-confirm');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.createVariable());
    }
    
    // 输入框回车键
    const input = document.getElementById('blocks-variable-name') as HTMLInputElement;
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.createVariable();
        } else if (e.key === 'Escape') {
          this.hideVariableModal();
        }
      });
    }
    
    // 点击遮罩关闭
    const modal = document.getElementById('blocks-variable-modal');
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.hideVariableModal();
        }
      });
    }
  }

  /**
   * 显示创建变量弹窗
   */
  private showVariableModal(): void {
    const modal = document.getElementById('blocks-variable-modal');
    const input = document.getElementById('blocks-variable-name') as HTMLInputElement;
    
    if (modal) {
      modal.classList.add('active');
    }
    if (input) {
      input.value = '';
      setTimeout(() => input.focus(), 100);
    }
  }

  /**
   * 隐藏创建变量弹窗
   */
  private hideVariableModal(): void {
    const modal = document.getElementById('blocks-variable-modal');
    if (modal) {
      modal.classList.remove('active');
    }
  }

  /**
   * 创建变量
   */
  private createVariable(): void {
    const input = document.getElementById('blocks-variable-name') as HTMLInputElement;
    if (!input || !this.workspace) return;
    
    const varName = input.value.trim();
    if (!varName) {
      input.focus();
      return;
    }
    
    // 检查变量名是否已存在
    const existingVars = this.workspace.getAllVariables();
    const exists = existingVars.some((v: any) => v.name === varName);
    if (exists) {
      input.style.borderColor = '#ef4444';
      setTimeout(() => {
        input.style.borderColor = '';
      }, 1000);
      return;
    }
    
    // 创建变量
    this.workspace.createVariable(varName);
    
    // 记录刚创建的变量名
    this.lastCreatedVarName = varName;
    
    // 关闭弹窗
    this.hideVariableModal();
    
    // 刷新变量分类显示
    setTimeout(() => this.showCategoryBlocks('variables'), 100);
  }

  /**
   * 绑定TAB切换事件
   */
  private bindTabEvents(): void {
    const tabs = document.querySelectorAll('.blocks-tab');
    const tabsContainer = document.querySelector('.blocks-tabs');
    
    if (!tabsContainer) return;
    
    // 创建指示器
    let indicator = tabsContainer.querySelector('.blocks-tab-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'blocks-tab-indicator';
      tabsContainer.insertBefore(indicator, tabsContainer.firstChild);
    }
    
    // 更新指示器位置
    const updateIndicator = (tab: HTMLElement) => {
      indicator.style.width = `${tab.offsetWidth}px`;
      indicator.style.left = `${tab.offsetLeft}px`;
    };
    
    // 初始化指示器位置
    const activeTab = tabsContainer.querySelector('.blocks-tab.active') as HTMLElement;
    if (activeTab) {
      setTimeout(() => updateIndicator(activeTab), 100);
    }
    
    // 绑定点击事件
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabId = target.dataset.tab;
        
        // 更新按钮状态
        tabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 更新指示器
        updateIndicator(target);
        
        // 切换视图
        const views = document.querySelectorAll('.blocks-view');
        views.forEach(view => {
          view.classList.toggle('active', (view as HTMLElement).dataset.view === tabId);
        });
        
        // 如果切换到编程视图，调整 Blockly 大小
        if (tabId === 'editor' && this.workspace) {
          setTimeout(() => Blockly.svgResize(this.workspace), 100);
        }
        
        // 如果切换到历史工程视图，渲染列表
        if (tabId === 'history') {
          this.renderHistoryList();
        }
      });
    });
  }

  /**
   * 渲染历史工程列表
   */
  private renderHistoryList(): void {
    const listEl = document.getElementById('blocks-history-list');
    const emptyEl = document.getElementById('blocks-history-empty');
    if (!listEl || !emptyEl) return;
    
    const projects = this.loadProjects();
    
    if (projects.length === 0) {
      listEl.classList.add('hide');
      emptyEl.classList.add('show');
      return;
    }
    
    listEl.classList.remove('hide');
    emptyEl.classList.remove('show');
    
    // 按更新时间排序（最新的在前面）
    projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    
    listEl.innerHTML = projects.map(project => {
      const spriteCount = project.sprites?.length || 0;
      return `
        <div class="history-project-card" data-id="${project.id}">
          <div class="history-card-header">
            <div class="history-card-icon">🧩</div>
            <div class="history-card-info">
              <div class="history-card-name">${this.escapeHtml(project.name)}</div>
              <div class="history-card-time">更新于 ${this.formatTime(project.updatedAt)}</div>
            </div>
          </div>
          <div class="history-card-stats">
            <div class="history-card-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span>${spriteCount} 个角色</span>
            </div>
            <div class="history-card-stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>创建于 ${this.formatDate(project.createdAt)}</span>
            </div>
          </div>
          <div class="history-card-actions">
            <button class="history-card-btn load" data-id="${project.id}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              加载项目
            </button>
            <button class="history-card-btn delete" data-id="${project.id}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
              删除
            </button>
          </div>
        </div>
      `;
    }).join('');
    
    // 绑定加载按钮事件
    listEl.querySelectorAll('.history-card-btn.load').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.loadProjectByIdAndSwitch(id);
      });
    });
    
    // 绑定删除按钮事件
    listEl.querySelectorAll('.history-card-btn.delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) {
          this.deleteProjectById(id);
          this.renderHistoryList(); // 重新渲染列表
        }
      });
    });
    
    // 点击卡片也可以加载
    listEl.querySelectorAll('.history-project-card').forEach(card => {
      card.addEventListener('click', (e) => {
        // 如果点击的是按钮，不触发卡片点击
        if ((e.target as HTMLElement).closest('.history-card-btn')) return;
        const id = (card as HTMLElement).dataset.id;
        if (id) this.loadProjectByIdAndSwitch(id);
      });
    });
  }
  
  /**
   * 加载项目并切换到编辑器
   */
  private loadProjectByIdAndSwitch(id: string): void {
    this.loadProjectById(id);
    
    // 切换到编辑器 TAB（大屏）
    const editorTab = document.querySelector('.blocks-tab[data-tab="editor"]') as HTMLElement;
    if (editorTab) {
      editorTab.click();
    }
    
    // 切换到编辑器 TAB（小屏竖向TAB）
    const editorVTab = document.querySelector('.blocks-vtab[data-vtab="editor"]') as HTMLElement;
    if (editorVTab) {
      editorVTab.click();
    }
    
    // 确保 Blockly 工作区正确显示
    setTimeout(() => {
      if (this.workspace) {
        Blockly.svgResize(this.workspace);
      }
    }, 150);
  }
  
  /**
   * 格式化日期
   */
  private formatDate(isoString: string): string {
    try {
      const date = new Date(isoString);
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    } catch {
      return '';
    }
  }

  /**
   * 渲染预设案例列表
   */
  private renderPresets(): void {
    const container = document.getElementById('blocks-presets-grid');
    if (!container) return;
    
    container.innerHTML = BLOCK_PRESETS.map(preset => `
      <div class="blocks-preset-card" data-preset="${preset.id}">
        <div class="blocks-preset-preview" style="background: ${preset.gradient}">
          <div class="blocks-preset-icon">${preset.icon}</div>
        </div>
        <div class="blocks-preset-info">
          <h3>${preset.name}</h3>
          <p>${preset.description}</p>
          <div class="blocks-preset-tags">
            <span class="blocks-preset-tag difficulty-${preset.difficulty}">${
              preset.difficulty === 'easy' ? '入门' : 
              preset.difficulty === 'medium' ? '进阶' : '挑战'
            }</span>
            ${preset.tags.map(tag => `<span class="blocks-preset-tag">${tag}</span>`).join('')}
          </div>
        </div>
      </div>
    `).join('');
    
    // 绑定卡片点击事件，打开详情弹窗
    container.querySelectorAll('.blocks-preset-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const presetId = (e.currentTarget as HTMLElement).dataset.preset;
        if (presetId) {
          this.showPresetDetail(presetId);
        }
      });
    });
    
    // 绑定详情弹窗事件
    this.bindPresetDetailEvents();
  }

  /**
   * 显示预设案例详情弹窗
   */
  private currentPresetId: string = '';
  
  private showPresetDetail(presetId: string): void {
    const preset = BLOCK_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    
    this.currentPresetId = presetId;
    
    // 填充弹窗内容
    const modal = document.getElementById('blocks-preset-detail-modal');
    const header = document.getElementById('preset-detail-header');
    const icon = document.getElementById('preset-detail-icon');
    const name = document.getElementById('preset-detail-name');
    const difficulty = document.getElementById('preset-detail-difficulty');
    const tags = document.getElementById('preset-detail-tags');
    const desc = document.getElementById('preset-detail-desc');
    const concepts = document.getElementById('preset-detail-concepts');
    const tips = document.getElementById('preset-detail-tips');
    const prereq = document.getElementById('preset-detail-prereq');
    const output = document.getElementById('preset-detail-output');
    
    if (!modal || !header || !icon || !name || !difficulty || !tags || !desc || !concepts || !tips) return;
    
    // 设置头部渐变背景
    header.style.background = preset.gradient;
    
    // 设置图标
    icon.innerHTML = preset.icon;
    icon.style.background = 'rgba(255, 255, 255, 0.2)';
    
    // 设置标题
    name.textContent = preset.name;
    
    // 设置难度
    const difficultyMap: Record<string, string> = {
      'easy': '入门',
      'medium': '进阶',
      'hard': '挑战'
    };
    difficulty.textContent = difficultyMap[preset.difficulty] || preset.difficulty;
    difficulty.className = `preset-detail-difficulty ${preset.difficulty}`;
    
    // 设置标签
    tags.innerHTML = preset.tags.map(tag => `<span>${tag}</span>`).join('');
    
    // 设置详细介绍
    desc.textContent = preset.detail;
    
    // 设置涉及概念
    concepts.innerHTML = preset.concepts.map(c => `
          <div class="concept-item">
            <div class="concept-name">${c.name}</div>
            <div class="concept-desc">${c.description}</div>
          </div>
        `).join('');
    
    // 设置学习提示
    tips.textContent = preset.tips;
    
    // 设置预计时间
    const timeEstimate = document.getElementById('preset-detail-time');
    if (timeEstimate) {
      timeEstimate.textContent = preset.timeEstimate || '';
    }
    
    // 设置前置知识
    if (prereq && preset.prerequisites) {
      prereq.innerHTML = preset.prerequisites.map(p => `<span>${p}</span>`).join('');
    }
    
    // 设置预期输出
    if (output) {
      output.textContent = preset.expectedOutput || '';
    }
    
    // 显示弹窗
    modal.classList.add('active');
  }

  /**
   * 隐藏预设案例详情弹窗
   */
  private hidePresetDetail(): void {
    const modal = document.getElementById('blocks-preset-detail-modal');
    if (modal) {
      modal.classList.remove('active');
    }
    this.currentPresetId = '';
  }

  /**
   * 绑定预设案例详情弹窗事件
   */
  private bindPresetDetailEvents(): void {
    const modal = document.getElementById('blocks-preset-detail-modal');
    const closeBtn = document.getElementById('preset-detail-close');
    const loadBtn = document.getElementById('preset-detail-load');
    
    // 关闭按钮
    closeBtn?.addEventListener('click', () => {
      this.hidePresetDetail();
    });
    
    // 点击背景关闭
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.hidePresetDetail();
      }
    });
    
    // 加载按钮
    loadBtn?.addEventListener('click', () => {
      if (this.currentPresetId) {
        this.loadPreset(this.currentPresetId);
        this.hidePresetDetail();
      }
    });
  }

  /**
   * 加载预设案例
   */
  private loadPreset(presetId: string): void {
    const preset = BLOCK_PRESETS.find(p => p.id === presetId);
    if (!preset || !this.workspace) return;
    
    // 清空当前工作区
    this.workspace.clear();
    
    // 清空画布和重置画笔状态
    this.clearCanvas();
    this.resetPenState();
    this.outputLines = [];
    
    // 加载 XML
    try {
      const xml = Blockly.utils.xml.textToDom(preset.blocks);
      Blockly.Xml.domToWorkspace(xml, this.workspace);
      
      // 延迟刷新工作区，解决积木块模糊问题
      requestAnimationFrame(() => {
        this.workspace?.resize();
        this.workspace?.scrollCenter();
      });
      
      // 更新项目名称为预设案例名称
      const nameInput = document.getElementById('blocks-project-name') as HTMLInputElement;
      if (nameInput) {
        nameInput.value = preset.name;
      }
      if (this.currentProject) {
        this.currentProject.name = preset.name;
        this.currentProject.id = this.generateId(); // 生成新ID，避免覆盖原有项目
      }
      
      // 切换到编程视图
      const editorTab = document.querySelector('.blocks-tab[data-tab="editor"]') as HTMLElement;
      if (editorTab) {
        editorTab.click();
      }
      
      // 小屏下同步切换竖向TAB到积木器
      const editorVtab = document.querySelector('.blocks-vtab[data-vtab="editor"]') as HTMLElement;
      if (editorVtab) {
        editorVtab.click();
      }
      
      console.log(`✅ 已加载预设案例: ${preset.name}`);
    } catch (e) {
      console.error('加载预设案例失败:', e);
    }
  }

  /**
   * 绑定筛选事件
   */
  private bindFilterEvents(): void {
    // 绑定难度筛选按钮
    const difficultyTags = document.querySelectorAll('#filter-difficulty .filter-tag');
    difficultyTags.forEach(tag => {
      tag.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const filter = target.dataset.filter || 'all';
        
        // 更新按钮状态
        difficultyTags.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 更新筛选状态
        this.filterState.difficulty = filter;
        this.applyFilters();
      });
    });
    
    // 绑定类型筛选按钮
    const categoryTags = document.querySelectorAll('#filter-category .filter-tag');
    categoryTags.forEach(tag => {
      tag.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const filter = target.dataset.filter || 'all';
        
        // 更新按钮状态
        categoryTags.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 更新筛选状态
        this.filterState.category = filter;
        this.applyFilters();
      });
    });
    
    // 绑定重置按钮
    const resetBtn = document.getElementById('filter-reset-btn');
    resetBtn?.addEventListener('click', () => this.resetFilters());
  }

  /**
   * 应用筛选
   */
  private applyFilters(): void {
    const cards = document.querySelectorAll('.blocks-preset-card');
    let visibleIndex = 0;
    
    cards.forEach(card => {
      const cardEl = card as HTMLElement;
      const presetId = cardEl.dataset.preset;
      const preset = BLOCK_PRESETS.find(p => p.id === presetId);
      if (!preset) return;
      
      // 检查难度筛选
      const difficultyMatch = this.filterState.difficulty === 'all' || 
                              preset.difficulty === this.filterState.difficulty;
      
      // 检查类型筛选（标签包含匹配）
      const categoryMatch = this.filterState.category === 'all' || 
                            preset.tags.includes(this.filterState.category);
      
      // 显示/隐藏卡片
      if (difficultyMatch && categoryMatch) {
        card.classList.remove('hidden');
        // 重置并重新触发动画，完整设置 animation 属性以覆盖 CSS 的 nth-child 规则
        cardEl.style.animation = 'none';
        cardEl.offsetHeight; // 强制回流
        cardEl.style.animation = `presetCardFadeIn 0.4s ease ${visibleIndex * 0.05}s forwards`;
        visibleIndex++;
      } else {
        card.classList.add('hidden');
      }
    });
    
    const visibleCount = visibleIndex;
    
    // 显示/隐藏空状态
    const emptyEl = document.getElementById('blocks-presets-empty');
    const gridEl = document.getElementById('blocks-presets-grid');
    if (emptyEl && gridEl) {
      if (visibleCount === 0) {
        emptyEl.style.display = 'block';
        gridEl.style.display = 'none';
      } else {
        emptyEl.style.display = 'none';
        gridEl.style.display = 'grid';
      }
    }
  }

  /**
   * 重置筛选
   */
  private resetFilters(): void {
    // 重置筛选状态
    this.filterState.difficulty = 'all';
    this.filterState.category = 'all';
    
    // 重置按钮状态
    document.querySelectorAll('#filter-difficulty .filter-tag').forEach((tag, i) => {
      tag.classList.toggle('active', i === 0);
    });
    document.querySelectorAll('#filter-category .filter-tag').forEach((tag, i) => {
      tag.classList.toggle('active', i === 0);
    });
    
    // 应用筛选
    this.applyFilters();
  }

  /**
   * 显示分类对应的积木（更新 Flyout 内容）
   */
  private showCategoryBlocks(categoryId: string): void {
    if (!this.workspace) return;

    const category = BLOCK_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return;

    let blockList: any[] = [];

    // 处理特殊分类（变量、函数）
    if (category.custom === 'VARIABLE') {
      // 变量分类：显示创建变量按钮 + 已有变量
      // 注册回调，使用自定义弹窗
      this.workspace.registerButtonCallback('CREATE_VARIABLE', () => {
        this.showVariableModal();
      });
      
      // 添加创建变量按钮
      blockList.push({ kind: 'button', text: '创建变量', callbackKey: 'CREATE_VARIABLE' });
      
      // 添加已有变量的积木
      const variables = this.workspace.getAllVariables();
      if (variables && variables.length > 0) {
        // 确定默认变量：优先使用刚创建的变量，否则使用最后一个变量
        let defaultVar = variables[variables.length - 1]; // 默认最后一个
        if (this.lastCreatedVarName) {
          const createdVar = variables.find((v: any) => v.name === this.lastCreatedVarName);
          if (createdVar) {
            defaultVar = createdVar;
          }
          // 清除记录，只在创建后首次刷新时使用
          this.lastCreatedVarName = '';
        }
        
        // 设置变量积木（指定默认变量）
        blockList.push({ 
          kind: 'block', 
          type: 'variables_set',
          fields: { VAR: { id: defaultVar.getId(), name: defaultVar.name, type: '' } }
        });
        blockList.push({ 
          kind: 'block', 
          type: 'variables_get',
          fields: { VAR: { id: defaultVar.getId(), name: defaultVar.name, type: '' } }
        });
        blockList.push({ 
          kind: 'block', 
          type: 'math_change',
          fields: { VAR: { id: defaultVar.getId(), name: defaultVar.name, type: '' } }
        });
      }
    } else if (category.custom === 'PROCEDURE') {
      // 函数分类
      this.workspace.registerButtonCallback('CREATE_PROCEDURE', () => {
        // 创建新函数
        const block = this.workspace.newBlock('procedures_defnoreturn');
        block.initSvg();
        block.render();
        block.moveBy(50, 50);
      });
      
      blockList.push({ kind: 'button', text: '创建函数', callbackKey: 'CREATE_PROCEDURE' });
      blockList.push({ kind: 'block', type: 'procedures_defnoreturn' });
      blockList.push({ kind: 'block', type: 'procedures_defreturn' });
      blockList.push({ kind: 'block', type: 'procedures_ifreturn' });
    } else {
      // 普通分类
      blockList = category.blocks.map(block => ({
        kind: 'block',
        type: block.type
      }));
    }

    // 更新工具箱（flyout）内容
    try {
      this.workspace.updateToolbox({
        kind: 'flyoutToolbox',
        contents: blockList
      });
      
      // 移除隐藏标记
      const container = document.getElementById('blocks-workspace');
      if (container) {
        container.classList.remove('flyout-hidden');
      }
      
      // 强制刷新 Flyout 滚动区域，解决首次展开不能滚动的问题
      setTimeout(() => {
        try {
          // 触发 workspace resize 以重新计算 Flyout 尺寸
          Blockly.svgResize(this.workspace);
          
          // 获取 Flyout 并强制刷新
          const flyout = this.workspace.getFlyout();
          if (flyout) {
            // 调用 reflow 重新计算滚动区域
            flyout.reflow();
          }
        } catch (e) {
          // 忽略刷新错误
        }
      }, 50);
    } catch (e) {
      console.warn('更新工具箱失败:', e);
    }
  }

  /**
   * 创建工具箱配置
   */
  private createToolbox(): object {
    return {
      kind: 'categoryToolbox',
      contents: [
        {
          kind: 'category',
          name: '🏃 运动',
          colour: '#4C97FF',
          contents: [
            { kind: 'block', type: 'controls_repeat_ext' },
            { kind: 'block', type: 'controls_whileUntil' },
          ]
        },
        {
          kind: 'category',
          name: '👀 外观',
          colour: '#9966FF',
          contents: [
            { kind: 'block', type: 'text_print' },
            { kind: 'block', type: 'text' },
          ]
        },
        {
          kind: 'category',
          name: '🔊 声音',
          colour: '#CF63CF',
          contents: []
        },
        {
          kind: 'category',
          name: '🎮 事件',
          colour: '#FFBF00',
          contents: []
        },
        {
          kind: 'category',
          name: '🔄 控制',
          colour: '#FFAB19',
          contents: [
            { kind: 'block', type: 'controls_if' },
            { kind: 'block', type: 'controls_repeat_ext' },
            { kind: 'block', type: 'controls_whileUntil' },
            { kind: 'block', type: 'controls_for' },
            { kind: 'block', type: 'controls_forEach' },
            { kind: 'block', type: 'controls_flow_statements' },
          ]
        },
        {
          kind: 'category',
          name: '➕ 运算',
          colour: '#59C059',
          contents: [
            { kind: 'block', type: 'math_number' },
            { kind: 'block', type: 'math_arithmetic' },
            { kind: 'block', type: 'math_single' },
            { kind: 'block', type: 'math_random_int' },
            { kind: 'block', type: 'logic_compare' },
            { kind: 'block', type: 'logic_operation' },
            { kind: 'block', type: 'logic_negate' },
            { kind: 'block', type: 'logic_boolean' },
          ]
        },
        {
          kind: 'category',
          name: '📦 变量',
          colour: '#FF8C1A',
          custom: 'VARIABLE'
        },
        {
          kind: 'category',
          name: '📋 列表',
          colour: '#FF661A',
          contents: [
            { kind: 'block', type: 'lists_create_empty' },
            { kind: 'block', type: 'lists_create_with' },
            { kind: 'block', type: 'lists_repeat' },
            { kind: 'block', type: 'lists_length' },
            { kind: 'block', type: 'lists_isEmpty' },
            { kind: 'block', type: 'lists_indexOf' },
            { kind: 'block', type: 'lists_getIndex' },
            { kind: 'block', type: 'lists_setIndex' },
          ]
        },
        {
          kind: 'category',
          name: '🔧 函数',
          colour: '#FF6680',
          custom: 'PROCEDURE'
        }
      ]
    };
  }

  /**
   * 创建 Blockly 主题
   */
  private createTheme(): any {
    return Blockly.Theme.defineTheme('codeverse', {
      base: Blockly.Themes.Classic,
      componentStyles: {
        workspaceBackgroundColour: '#1e1e2e',
        toolboxBackgroundColour: '#161622',
        toolboxForegroundColour: '#e6edf3',
        flyoutBackgroundColour: '#21262d',
        flyoutForegroundColour: '#e6edf3',
        flyoutOpacity: 0.95,
        scrollbarColour: '#484f58',
        insertionMarkerColour: '#7c8aff',
        insertionMarkerOpacity: 0.3,
        scrollbarOpacity: 0.6,
        cursorColour: '#7c8aff'
      },
      fontStyle: {
        family: '"Inter", "PingFang SC", sans-serif',
        size: 12
      }
    });
  }

  /**
   * 更新代码预览
   */
  private updateCodePreview(): void {
    if (!this.workspace) return;
    
    const container = document.getElementById('blocks-code-content');
    if (!container) return;
    
    try {
      let code: string;
      let mode: string;
      
      const jsGenerator = (Blockly as any).JavaScript;
      const pyGenerator = (Blockly as any).Python;
      
      // 保存并清除 STATEMENT_PREFIX，生成干净的预览代码
      const savedPrefix = jsGenerator?.STATEMENT_PREFIX;
      if (jsGenerator) jsGenerator.STATEMENT_PREFIX = '';
      
      if (this.settings.codeLanguage === 'python') {
        code = pyGenerator?.workspaceToCode(this.workspace) || '# Python 生成器未加载';
        mode = 'python';
      } else {
        code = jsGenerator?.workspaceToCode(this.workspace) || '// JavaScript 生成器未加载';
        mode = 'javascript';
      }
      
      // 恢复 STATEMENT_PREFIX
      if (jsGenerator && savedPrefix) jsGenerator.STATEMENT_PREFIX = savedPrefix;
      
      if (!code.trim()) {
        code = this.settings.codeLanguage === 'python' ? '# 拖拽积木开始编程...' : '// 拖拽积木开始编程...';
      }
      
      // 初始化或更新 CodeMirror
      if (!this.codePreviewEditor) {
        // 清空容器
        container.innerHTML = '';
        
        // 创建 CodeMirror 实例（只读模式）
        this.codePreviewEditor = (window as any).CodeMirror(container, {
          value: code,
          mode: mode,
          theme: 'dracula',
          readOnly: true,
          lineNumbers: true,
          lineWrapping: true,
          viewportMargin: Infinity,
          tabSize: 2,
          indentUnit: 2
        });
        
        // 延迟刷新，确保布局正确
        setTimeout(() => this.codePreviewEditor?.refresh(), 50);
      } else {
        // 更新内容和模式
        this.codePreviewEditor.setValue(code);
        this.codePreviewEditor.setOption('mode', mode);
        
        // 刷新布局，解决行号与代码重叠问题
        requestAnimationFrame(() => this.codePreviewEditor?.refresh());
      }
    } catch (e) {
      console.error('代码预览更新失败:', e);
    }
  }

  /**
   * HTML 转义
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * 创建默认项目
   */
  private createDefaultProject(): void {
    const defaultSprite: Sprite = {
      id: 'sprite_1',
      name: '小猫',
      icon: '🐱',
      x: 0,
      y: 0,
      direction: 90,
      visible: true
    };
    
    this.sprites.set(defaultSprite.id, defaultSprite);
    this.currentSprite = defaultSprite;
    
    this.currentProject = {
      id: this.generateId(),
      name: '我的项目',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      sprites: [defaultSprite]
    };
  }

  /**
   * 生成唯一 ID
   */
  private generateId(): string {
    return 'blk_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }

  /**
   * 渲染角色列表
   */
  private renderSpritesList(): void {
    const listEl = document.getElementById('blocks-sprites-list');
    if (!listEl) return;
    
    listEl.innerHTML = '';
    
    this.sprites.forEach(sprite => {
      const item = document.createElement('div');
      item.className = `blocks-sprite-item${sprite.id === this.currentSprite?.id ? ' active' : ''}`;
      item.dataset.spriteId = sprite.id;
      item.innerHTML = `
        <div class="blocks-sprite-icon">${sprite.icon}</div>
        <div class="blocks-sprite-name">${sprite.name}</div>
      `;
      item.addEventListener('click', () => this.selectSprite(sprite.id));
      listEl.appendChild(item);
    });
    
    // 重绘画布
    this.clearCanvas();
  }

  /**
   * 选择角色
   */
  private selectSprite(spriteId: string): void {
    // 保存当前角色的工作区
    this.saveCurrentSpriteWorkspace();
    
    // 切换角色
    const sprite = this.sprites.get(spriteId);
    if (!sprite) return;
    
    this.currentSprite = sprite;
    
    // 加载新角色的工作区
    this.loadSpriteWorkspace(sprite);
    
    // 更新 UI
    this.renderSpritesList();
  }

  /**
   * 保存当前角色的工作区
   */
  private saveCurrentSpriteWorkspace(): void {
    if (!this.workspace || !this.currentSprite) return;
    
    const xml = Blockly.Xml.workspaceToDom(this.workspace);
    this.currentSprite.workspace = Blockly.Xml.domToText(xml);
  }

  /**
   * 加载角色工作区
   */
  private loadSpriteWorkspace(sprite: Sprite): void {
    if (!this.workspace) return;
    
    // 清空工作区
    this.workspace.clear();
    
    // 加载保存的工作区
    if (sprite.workspace) {
      try {
        const xml = Blockly.utils.xml.textToDom(sprite.workspace);
        Blockly.Xml.domToWorkspace(xml, this.workspace);
        
        // 延迟刷新工作区，解决积木块模糊问题
        requestAnimationFrame(() => {
          this.workspace?.resize();
        });
      } catch (e) {
        console.warn('加载工作区失败:', e);
      }
    }
  }

  /**
   * 添加新角色 - 弹出选择弹窗
   */
  private addSprite(): void {
    console.log('🟢 addSprite 被调用');
    this.showAddSpriteModal();
  }
  
  /**
   * 显示添加角色弹窗
   */
  private showAddSpriteModal(): void {
    console.log('🟢 showAddSpriteModal 被调用');
    const modal = document.getElementById('blocks-add-sprite-modal');
    console.log('🟢 modal 元素:', modal);
    if (!modal) {
      console.error('❌ 找不到 blocks-add-sprite-modal 元素');
      return;
    }
    
    // 重置表单
    const nameInput = modal.querySelector('#sprite-name-input') as HTMLInputElement;
    if (nameInput) nameInput.value = '';
    
    // 取消所有选中状态
    modal.querySelectorAll('.sprite-icon-option').forEach(opt => opt.classList.remove('selected'));
    // 默认选中第一个
    const firstOption = modal.querySelector('.sprite-icon-option');
    firstOption?.classList.add('selected');
    
    modal.classList.add('active');
  }
  
  /**
   * 确认创建角色
   */
  private confirmAddSprite(): void {
    const modal = document.getElementById('blocks-add-sprite-modal');
    if (!modal) return;
    
    const nameInput = modal.querySelector('#sprite-name-input') as HTMLInputElement;
    const selectedIcon = modal.querySelector('.sprite-icon-option.selected') as HTMLElement;
    
    const name = nameInput?.value.trim() || '新角色';
    const icon = selectedIcon?.dataset.icon || '🐱';
    
    const id = this.generateId();
    const newSprite: Sprite = {
      id,
      name,
      icon,
      x: 0,
      y: 0,
      direction: 90,
      visible: true
    };
    
    this.sprites.set(id, newSprite);
    this.selectSprite(id);
    
    // 关闭弹窗
    modal.classList.remove('active');
    
    this.showToast(`角色「${name}」已创建`, 'success');
  }
  
  /**
   * 绑定添加角色弹窗事件
   */
  private bindAddSpriteModalEvents(): void {
    const modal = document.getElementById('blocks-add-sprite-modal');
    if (!modal) return;
    
    // 图标选择
    modal.querySelectorAll('.sprite-icon-option').forEach(option => {
      option.addEventListener('click', () => {
        modal.querySelectorAll('.sprite-icon-option').forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');
      });
    });
    
    // 确认按钮
    const confirmBtn = modal.querySelector('#sprite-confirm-btn');
    confirmBtn?.addEventListener('click', () => this.confirmAddSprite());
    
    // 取消按钮
    const cancelBtn = modal.querySelector('#sprite-cancel-btn');
    cancelBtn?.addEventListener('click', () => modal.classList.remove('active'));
    
    // 点击遮罩层关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.remove('active');
    });
  }

  /**
   * 绑定事件
   */
  private bindEvents(): void {
    // 添加角色按钮 - 使用事件委托确保能捕获到
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      // 添加角色按钮
      const addBtn = target.closest('#blocks-add-sprite');
      if (addBtn) {
        console.log('🟢 点击了添加角色按钮');
        e.preventDefault();
        e.stopPropagation();
        this.addSprite();
        return;
      }
      
      // 舞台类型切换按钮
      const stageTypeBtn = target.closest('.stage-type-btn') as HTMLElement;
      if (stageTypeBtn) {
        const type = stageTypeBtn.dataset.type as StageType;
        if (type) {
          this.setStageType(type);
        }
      }
    });
    
    // 自定义缩放控件
    const zoomInBtn = document.getElementById('blocks-zoom-in');
    const zoomOutBtn = document.getElementById('blocks-zoom-out');
    const zoomResetBtn = document.getElementById('blocks-zoom-reset');
    const deleteBtn = document.getElementById('blocks-delete');
    
    zoomInBtn?.addEventListener('click', () => {
      if (this.workspace) {
        this.workspace.zoomCenter(1);
      }
    });
    
    zoomOutBtn?.addEventListener('click', () => {
      if (this.workspace) {
        this.workspace.zoomCenter(-1);
      }
    });
    
    zoomResetBtn?.addEventListener('click', () => {
      if (this.workspace) {
        this.workspace.setScale(0.9);
        this.workspace.scrollCenter();
      }
    });
    
    // 删除按钮 - 使用 mousedown 在焦点转移前捕获选中的积木
    let blockToDelete: any = null;
    deleteBtn?.addEventListener('mousedown', (e) => {
      e.preventDefault(); // 阻止焦点转移
      blockToDelete = this.selectedBlock;
    });
    
    deleteBtn?.addEventListener('click', () => {
      if (this.workspace && blockToDelete) {
        if (blockToDelete.isDeletable && blockToDelete.isDeletable()) {
          blockToDelete.dispose(true, true);
          this.selectedBlock = null;
        }
        blockToDelete = null;
      }
    });
    
    // 运行/暂停按钮（合二为一）
    const runBtn = document.getElementById('blocks-run');
    runBtn?.addEventListener('click', () => this.toggleRunPause());
    
    // 停止按钮
    const stopBtn = document.getElementById('blocks-stop');
    stopBtn?.addEventListener('click', () => this.stopCode());
    
    // 速度控制按钮
    document.querySelectorAll('.blocks-speed-control .speed-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const speed = parseInt(target.dataset.speed || '0');
        this.executionSpeed = speed;
        // 更新按钮状态
        document.querySelectorAll('.blocks-speed-control .speed-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
        // 更新状态栏
        this.updateStatusBar();
      });
    });
    
    // 复制代码按钮
    const copyBtn = document.getElementById('blocks-copy-code');
    copyBtn?.addEventListener('click', () => this.copyCode());
    
    // 新建项目
    const newBtn = document.getElementById('blocks-new-project');
    newBtn?.addEventListener('click', () => this.newProject());
    
    // 保存项目
    const saveBtn = document.getElementById('blocks-save-project');
    saveBtn?.addEventListener('click', () => this.saveProject());
    
    // 项目名称输入 - 使用 input 事件实时同步
    const nameInput = document.getElementById('blocks-project-name') as HTMLInputElement;
    nameInput?.addEventListener('input', () => {
      if (this.currentProject) {
        this.currentProject.name = nameInput.value || '我的项目';
      }
    });
  }

  /**
   * 绑定小屏竖向TAB事件
   */
  private bindVerticalTabEvents(): void {
    const vtabs = document.querySelectorAll('.blocks-vtab');
    const sidebar = document.querySelector('.blocks-sidebar') as HTMLElement;
    const workspaceContainer = document.querySelector('.blocks-workspace-container') as HTMLElement;
    const stagePanel = document.querySelector('.blocks-stage-panel') as HTMLElement;
    const editorView = document.getElementById('blocks-view-editor');
    const presetsView = document.getElementById('blocks-view-presets');
    const historyView = document.getElementById('blocks-view-history');
    
    vtabs.forEach(vtab => {
      vtab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.vtab;
        
        // 更新TAB激活状态
        vtabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 处理预设案例和我的工程（切换到其他顶级视图）
        if (tabName === 'presets') {
          editorView?.classList.remove('active');
          historyView?.classList.remove('active');
          presetsView?.classList.add('active');
          // 小屏下隐藏积木器相关面板
          sidebar?.classList.add('sm-hidden');
          workspaceContainer?.classList.add('sm-hidden');
          stagePanel?.classList.add('sm-hidden');
          return;
        }
        
        if (tabName === 'history') {
          editorView?.classList.remove('active');
          presetsView?.classList.remove('active');
          historyView?.classList.add('active');
          // 小屏下隐藏积木器相关面板，让工程页面全屏显示
          sidebar?.classList.add('sm-hidden');
          workspaceContainer?.classList.add('sm-hidden');
          stagePanel?.classList.add('sm-hidden');
          // 渲染工程列表
          this.renderHistoryList();
          return;
        }
        
        // 其他TAB都在编程视图内
        editorView?.classList.add('active');
        presetsView?.classList.remove('active');
        historyView?.classList.remove('active');
        
        // 小屏下复用大屏组件，通过显示/隐藏切换
        if (tabName === 'editor') {
          // 积木器：显示左侧面板 + 工作区，隐藏舞台面板
          sidebar?.classList.remove('sm-hidden');
          workspaceContainer?.classList.remove('sm-hidden');
          stagePanel?.classList.add('sm-hidden');
          stagePanel?.classList.remove('sm-fullscreen');
        } else if (tabName === 'stage') {
          // 舞台：隐藏左侧面板 + 工作区，显示舞台面板（全屏）
          sidebar?.classList.add('sm-hidden');
          workspaceContainer?.classList.add('sm-hidden');
          stagePanel?.classList.remove('sm-hidden');
          stagePanel?.classList.add('sm-fullscreen');
        } else if (tabName === 'code') {
          // 代码：隐藏左侧面板 + 工作区，显示代码预览（全屏）
          sidebar?.classList.add('sm-hidden');
          workspaceContainer?.classList.add('sm-hidden');
          stagePanel?.classList.remove('sm-hidden');
          stagePanel?.classList.add('sm-fullscreen');
          stagePanel?.classList.add('sm-code-mode');
          
          // 刷新CodeMirror编辑器显示
          setTimeout(() => {
            this.codePreviewEditor?.refresh();
          }, 100);
        }
        
        // 如果不是代码模式，移除代码模式标记
        if (tabName !== 'code') {
          stagePanel?.classList.remove('sm-code-mode');
        }
      });
    });
  }

  /**
   * 运行代码
   */
  private async runCode(): Promise<void> {
    if (!this.workspace || this.isRunning) return;
    
    this.isRunning = true;
    this.shouldStop = false;
    this.isPaused = false;
    this.outputLines = [];
    
    // 更新运行按钮状态
    this.updateRunButtonIcon();
    
    try {
      const code = Blockly.JavaScript?.workspaceToCode(this.workspace);
      if (!code) {
        this.isRunning = false;
        this.updateRunButtonIcon();
        return;
      }
      
      // 自动检测舞台类型
      const hasPenCommands = /pen(Down|Up|Move|Goto|TurnRight|TurnLeft|SetColor|SetSize|Clear)/.test(code);
      const hasOutputCommands = /output\(/.test(code);
      const hasSpriteCommands = /sprite(Move|TurnRight|TurnLeft|Goto|GotoRandom|SetX|SetY|ChangeX|ChangeY)/.test(code);
      
      // 优先级：画笔 > 角色 > 文字输出
      if (hasPenCommands) {
        this.setStageType('pen');
      } else if (hasSpriteCommands) {
        this.setStageType('sprite');
      } else if (hasOutputCommands) {
        this.setStageType('text');
      }
      
      if (this.stageType !== 'text') {
        this.clearCanvas();
        this.resetPenState();
      }
      
      console.log('执行代码:', code);
      
      // 根据速度设置延时
      const delays = [0, 200, 500]; // 快速、正常、慢速
      const delayMs = delays[this.executionSpeed] || 0;
      
      // 创建安全的执行环境
      const outputFn = (value: any) => {
        this.outputLines.push(String(value));
        this.renderOutput();
      };
      
      // 等待函数
      const waitSeconds = (seconds: number): Promise<void> => {
        return new Promise(resolve => {
          setTimeout(() => {
            if (!this.shouldStop) resolve();
          }, seconds * 1000);
        });
      };
      
      const waitUntil = (conditionFn: () => boolean): Promise<void> => {
        return new Promise(resolve => {
          const check = () => {
            if (this.shouldStop) return;
            if (conditionFn()) {
              resolve();
            } else {
              requestAnimationFrame(check);
            }
          };
          check();
        });
      };
      
      // 检测函数
      const keysPressed: Set<string> = new Set();
      let timerStart = Date.now();
      
      // 键盘事件监听
      const handleKeyDown = (e: KeyboardEvent) => { keysPressed.add(e.key === ' ' ? 'space' : e.key); };
      const handleKeyUp = (e: KeyboardEvent) => { keysPressed.delete(e.key === ' ' ? 'space' : e.key); };
      
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keyup', handleKeyUp);
      
      const isKeyPressed = (key: string) => keysPressed.has(key);
      const getTimer = () => (Date.now() - timerStart) / 1000;
      const resetTimer = () => { timerStart = Date.now(); };
      
      // 画笔函数绑定
      const penDown = () => { this.penState.isDown = true; };
      const penUp = () => { this.penState.isDown = false; };
      const penClear = () => { this.clearCanvas(); this.resetPenState(); };
      const penMove = (steps: number) => { this.penMove(steps); };
      const penGoto = (x: number, y: number) => { this.penGoto(x, y); };
      const penTurnRight = (angle: number) => { this.penState.direction += angle; };
      const penTurnLeft = (angle: number) => { this.penState.direction -= angle; };
      const penSetColor = (color: string) => { this.penState.color = color; };
      const penSetSize = (size: number) => { this.penState.size = size; };
      
      // 绘图增强函数
      let penFillMode = false;
      const penSetFill = (fill: boolean) => { penFillMode = fill; };
      
      const penDrawCircle = (radius: number) => {
        if (!this.ctx) return;
        const x = this.penState.x;
        const y = this.penState.y;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        if (penFillMode) {
          this.ctx.fillStyle = this.penState.color;
          this.ctx.fill();
        } else {
          this.ctx.strokeStyle = this.penState.color;
          this.ctx.lineWidth = this.penState.size;
          this.ctx.stroke();
        }
      };
      
      const penDrawRect = (width: number, height: number) => {
        if (!this.ctx) return;
        const x = this.penState.x - width / 2;
        const y = this.penState.y - height / 2;
        if (penFillMode) {
          this.ctx.fillStyle = this.penState.color;
          this.ctx.fillRect(x, y, width, height);
        } else {
          this.ctx.strokeStyle = this.penState.color;
          this.ctx.lineWidth = this.penState.size;
          this.ctx.strokeRect(x, y, width, height);
        }
      };
      
      // 声音函数
      let audioVolume = 100;
      const playSound = (sound: string) => {
        try {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          const oscillator = audioCtx.createOscillator();
          const gainNode = audioCtx.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioCtx.destination);
          gainNode.gain.value = audioVolume / 100 * 0.3;
          
          // 不同声音类型
          const soundTypes: Record<string, { freq: number; duration: number }> = {
            beep: { freq: 800, duration: 0.2 },
            success: { freq: 600, duration: 0.3 },
            warning: { freq: 400, duration: 0.4 },
            error: { freq: 200, duration: 0.5 },
            click: { freq: 1000, duration: 0.1 }
          };
          
          const config = soundTypes[sound] || soundTypes.beep;
          oscillator.frequency.value = config.freq;
          oscillator.start();
          oscillator.stop(audioCtx.currentTime + config.duration);
        } catch (e) {
          console.log('声音播放失败:', e);
        }
      };
      const stopAllSounds = () => { /* 简化实现 */ };
      const setVolume = (vol: number) => { audioVolume = Math.max(0, Math.min(100, vol)); };
      
      // 角色运动函数（使用 Scratch 风格坐标系：中心 0,0，X范围[-240,240]，Y范围[-180,180]，Y向上为正）
      const spriteState = { x: 0, y: 0, direction: 90 };  // 角色状态（Scratch坐标）
      const CANVAS_WIDTH = 480;
      const CANVAS_HEIGHT = 360;
      const HALF_WIDTH = CANVAS_WIDTH / 2;   // 240
      const HALF_HEIGHT = CANVAS_HEIGHT / 2; // 180
      
      // 边界限制函数：防止角色超出画布
      const clampPosition = () => {
        spriteState.x = Math.max(-HALF_WIDTH + 20, Math.min(HALF_WIDTH - 20, spriteState.x));
        spriteState.y = Math.max(-HALF_HEIGHT + 20, Math.min(HALF_HEIGHT - 20, spriteState.y));
      };
      
      const spriteMove = (steps: number) => {
        const rad = (spriteState.direction - 90) * Math.PI / 180;
        spriteState.x += Math.cos(rad) * steps;
        spriteState.y -= Math.sin(rad) * steps;  // Scratch Y向上为正
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteTurnRight = (angle: number) => {
        spriteState.direction += angle;
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteTurnLeft = (angle: number) => {
        spriteState.direction -= angle;
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteGoto = (x: number, y: number) => {
        spriteState.x = x;
        spriteState.y = y;
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteGotoRandom = () => {
        // 随机位置：X [-200, 200], Y [-140, 140]
        spriteState.x = Math.random() * 400 - 200;
        spriteState.y = Math.random() * 280 - 140;
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteSetX = (x: number) => {
        spriteState.x = x;
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteSetY = (y: number) => {
        spriteState.y = y;
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteChangeX = (dx: number) => {
        spriteState.x += dx;
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteChangeY = (dy: number) => {
        spriteState.y += dy;
        clampPosition();
        this.updateSpriteDisplay(spriteState);
      };
      
      // 角色外观函数
      let spriteVisible = true;
      
      const spriteShow = () => {
        spriteVisible = true;
        this.updateSpriteDisplay(spriteState);
      };
      
      const spriteHide = () => {
        spriteVisible = false;
        // 清除角色显示
        this.clearCanvas();
      };
      
      const spriteSay = async (text: string, secs: number): Promise<void> => {
        // 在角色上方显示说话气泡
        if (!this.ctx) return;
        const x = spriteState.x;
        const y = spriteState.y - 30;
        
        // 画气泡背景
        this.ctx.fillStyle = 'white';
        this.ctx.strokeStyle = '#333';
        this.ctx.lineWidth = 2;
        const textWidth = this.ctx.measureText(String(text)).width;
        const bubbleWidth = Math.max(textWidth + 20, 60);
        const bubbleHeight = 30;
        const bubbleX = x - bubbleWidth / 2;
        const bubbleY = y - bubbleHeight;
        
        // 圆角矩形
        this.ctx.beginPath();
        this.ctx.roundRect(bubbleX, bubbleY, bubbleWidth, bubbleHeight, 8);
        this.ctx.fill();
        this.ctx.stroke();
        
        // 文字
        this.ctx.fillStyle = '#333';
        this.ctx.font = '14px sans-serif';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(String(text), x, bubbleY + bubbleHeight / 2);
        
        // 等待指定时间
        await new Promise(resolve => setTimeout(resolve, secs * 1000));
        
        // 清除气泡（重绘画布）
        this.clearCanvas();
        if (spriteVisible) {
          this.updateSpriteDisplay(spriteState);
        }
      };
      
      // 创建高亮函数（包含延时和暂停检查）
      const that = this;
      const highlightBlock = async (blockId: string) => {
        if (that.shouldStop) throw new Error('stopped');
        
        // 检查暂停状态
        while (that.isPaused && !that.shouldStop) {
          await new Promise<void>(resolve => {
            that.pauseResolve = resolve;
          });
        }
        if (that.shouldStop) throw new Error('stopped');
        
        if (delayMs > 0 && that.workspace) {
          that.workspace.highlightBlock(blockId);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      };
      
      // 构建异步可执行代码
      const execCode = `
        (async function(output, highlightBlock, penDown, penUp, penClear, penMove, penGoto, penTurnRight, penTurnLeft, penSetColor, penSetSize, penDrawCircle, penDrawRect, penSetFill, playSound, stopAllSounds, setVolume, spriteMove, spriteTurnRight, spriteTurnLeft, spriteGoto, spriteGotoRandom, spriteSetX, spriteSetY, spriteChangeX, spriteChangeY, spriteShow, spriteHide, spriteSay, waitSeconds, waitUntil, isKeyPressed, getTimer, resetTimer) {
          ${code}
        })
      `;
      
      // 执行代码
      const fn = eval(execCode);
      try {
        await fn(outputFn, highlightBlock, penDown, penUp, penClear, penMove, penGoto, penTurnRight, penTurnLeft, penSetColor, penSetSize, penDrawCircle, penDrawRect, penSetFill, playSound, stopAllSounds, setVolume, spriteMove, spriteTurnRight, spriteTurnLeft, spriteGoto, spriteGotoRandom, spriteSetX, spriteSetY, spriteChangeX, spriteChangeY, spriteShow, spriteHide, spriteSay, waitSeconds, waitUntil, isKeyPressed, getTimer, resetTimer);
      } finally {
        // 清理事件监听器
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('keyup', handleKeyUp);
      }
      
    } catch (e) {
      // 忽略用户主动停止的情况
      if (e instanceof Error && e.message === 'stopped') {
        console.log('ℹ️ 用户停止了代码执行');
      } else {
        console.error('代码执行错误:', e);
        this.outputLines.push(`错误: ${e}`);
        this.renderOutput();
      }
    }
    
    // 清除高亮
    if (this.workspace) {
      this.workspace.highlightBlock(null);
    }
    
    this.isRunning = false;
    this.updateRunButtonIcon();
  }
  
  /**
   * 设置舞台类型
   */
  private setStageType(type: StageType): void {
    this.stageType = type;
    
    // 更新按钮状态
    document.querySelectorAll('.stage-type-btn').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.type === type);
    });
    
    // 切换显示区域
    const canvas = document.getElementById('blocks-canvas') as HTMLCanvasElement;
    const textOutput = document.getElementById('blocks-text-output');
    const rulerX = document.getElementById('canvas-ruler-x');
    const rulerY = document.getElementById('canvas-ruler-y');
    const canvasControls = document.querySelector('.canvas-controls') as HTMLElement;
    
    if (type === 'text') {
      // 文字输出模式：隐藏画布、刻度尺和整个工具栏，显示文字输出
      if (canvas) canvas.style.display = 'none';
      if (textOutput) textOutput.style.display = 'block';
      if (rulerX) rulerX.style.display = 'none';
      if (rulerY) rulerY.style.display = 'none';
      if (canvasControls) canvasControls.style.display = 'none';
    } else {
      // 画笔/角色模式：显示画布和工具栏，隐藏文字输出
      if (canvas) canvas.style.display = 'block';
      if (textOutput) textOutput.style.display = 'none';
      
      // 根据设置决定是否显示尺子
      const showRuler = this.settings.rulerEnabled;
      if (rulerX) rulerX.style.display = showRuler ? 'block' : 'none';
      if (rulerY) rulerY.style.display = showRuler ? 'block' : 'none';
      if (canvasControls) canvasControls.style.display = 'flex';
      
      // 重新绘制画布
      this.clearCanvas();
      this.resetPenState();
      
      // 刷新到度尺
      this.renderRulers();
    }
    
    // 更新状态栏
    this.updateStatusBar();
  }
  
  /**
   * 重置画笔状态
   */
  private resetPenState(): void {
    // 使用用户设置的起点，否则使用画布中心
    const startX = this.userStartPoint?.x ?? (this.CANVAS_WIDTH / 2);
    const startY = this.userStartPoint?.y ?? (this.CANVAS_HEIGHT / 2);
    
    this.penState = {
      isDown: false,
      x: startX,
      y: startY,
      direction: 0,
      color: '#00ff00',
      size: 2
    };
  }
  
  /**
   * 画笔移动
   */
  private penMove(steps: number): void {
    if (!this.ctx) return;
    
    const rad = (this.penState.direction - 90) * Math.PI / 180;
    const newX = this.clampX(this.penState.x + Math.cos(rad) * steps);
    const newY = this.clampY(this.penState.y + Math.sin(rad) * steps);
    
    if (this.penState.isDown) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = this.penState.color;
      this.ctx.lineWidth = this.penState.size;
      this.ctx.lineCap = 'round';
      this.ctx.moveTo(this.penState.x, this.penState.y);
      this.ctx.lineTo(newX, newY);
      this.ctx.stroke();
    }
    
    this.penState.x = newX;
    this.penState.y = newY;
  }
  
  /**
   * 画笔移到指定坐标
   */
  private penGoto(x: number, y: number): void {
    if (!this.ctx) return;
    
    // 限制坐标在画布范围内
    const clampedX = this.clampX(x);
    const clampedY = this.clampY(y);
    
    if (this.penState.isDown) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = this.penState.color;
      this.ctx.lineWidth = this.penState.size;
      this.ctx.lineCap = 'round';
      this.ctx.moveTo(this.penState.x, this.penState.y);
      this.ctx.lineTo(clampedX, clampedY);
      this.ctx.stroke();
    }
    
    this.penState.x = clampedX;
    this.penState.y = clampedY;
  }
  
  /**
   * 限制X坐标在画布范围内
   */
  private clampX(x: number): number {
    return Math.max(0, Math.min(this.CANVAS_WIDTH, x));
  }
  
  /**
   * 限制Y坐标在画布范围内
   */
  private clampY(y: number): number {
    return Math.max(0, Math.min(this.CANVAS_HEIGHT, y));
  }

  /**
   * 渲染输出到画布
   */
  private renderOutput(): void {
    // 文字输出模式：使用专用文字容器
    if (this.stageType === 'text') {
      const textOutput = document.getElementById('blocks-text-output');
      if (textOutput) {
        textOutput.innerHTML = this.outputLines.map(line => `<div>> ${line}</div>`).join('');
        textOutput.scrollTop = textOutput.scrollHeight;
      }
      return;
    }
    
    // 画布模式：在画布上绘制输出
    const canvas = document.getElementById('blocks-canvas') as HTMLCanvasElement;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // 清空画布
    ctx.fillStyle = '#1e1e2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // 绘制输出文本
    ctx.fillStyle = '#a6e3a1';
    ctx.font = '16px Consolas, monospace';
    
    const startY = 30;
    const lineHeight = 24;
    const maxLines = Math.floor((canvas.height - 20) / lineHeight);
    
    // 只显示最后 N 行
    const lines = this.outputLines.slice(-maxLines);
    
    lines.forEach((line, index) => {
      ctx.fillText(`> ${line}`, 20, startY + index * lineHeight);
    });
  }

  /**
   * 停止代码执行
   */
  private stopCode(): void {
    this.isRunning = false;
    this.shouldStop = true;
    
    // 清除高亮
    if (this.workspace) {
      this.workspace.highlightBlock(null);
    }
    
    // 移除运行按钮状态
    const runBtn = document.getElementById('blocks-run');
    runBtn?.classList.remove('running');
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    
    // 重置所有角色位置
    this.sprites.forEach(sprite => {
      sprite.x = 0;
      sprite.y = 0;
      sprite.direction = 90;
    });
    
    this.clearCanvas();
    
    // 重置暂停状态
    this.isPaused = false;
    if (this.pauseResolve) {
      this.pauseResolve();
      this.pauseResolve = null;
    }
    
    // 重置运行按钮图标
    this.updateRunButtonIcon();
  }

  /**
   * 更新运行按钮图标状态
   */
  private updateRunButtonIcon(): void {
    const runBtn = document.getElementById('blocks-run');
    const playIcon = runBtn?.querySelector('.icon-play') as HTMLElement;
    const pauseIcon = runBtn?.querySelector('.icon-pause') as HTMLElement;
    
    if (this.isRunning && !this.isPaused) {
      // 运行中：显示暂停图标
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
      runBtn?.classList.add('running');
    } else {
      // 未运行或已暂停：显示播放图标
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
      if (!this.isRunning) {
        runBtn?.classList.remove('running');
      }
    }
    
    // 同步更新状态栏
    this.updateStatusBar();
  }

  /**
   * 更新状态栏显示（更新全局底部状态栏的舞台信息）
   */
  private updateStatusBar(): void {
    // 只有当积木页面处于活动状态时才显示舞台信息
    const blocksPage = document.getElementById('page-blocks');
    const isBlocksActive = blocksPage?.classList.contains('active');
    
    const blocksStatusEl = document.getElementById('status-blocks');
    if (blocksStatusEl) {
      blocksStatusEl.style.display = isBlocksActive ? 'inline' : 'none';
    }
    
    // 如果不是积木模式，不更新其他信息
    if (!isBlocksActive) return;
    
    // 发光提醒函数
    const triggerGlow = (el: HTMLElement) => {
      el.classList.remove('status-glow');
      void el.offsetWidth; // 强制重绘
      el.classList.add('status-glow');
    };
    
    // 更新舞台类型
    const stageTypeEl = document.getElementById('status-stage-type');
    if (stageTypeEl) {
      const modeNames: Record<StageType, string> = {
        'pen': '画笔',
        'sprite': '角色',
        'text': '文字'
      };
      const newText = `舞台: ${modeNames[this.stageType]}`;
      if (stageTypeEl.textContent !== newText) {
        stageTypeEl.textContent = newText;
        triggerGlow(stageTypeEl);
      }
    }
    
    // 更新执行速度
    const stageSpeedEl = document.getElementById('status-stage-speed');
    if (stageSpeedEl) {
      const speedNames = ['快速', '正常', '慢速'];
      const newText = `速度: ${speedNames[this.executionSpeed]}`;
      if (stageSpeedEl.textContent !== newText) {
        stageSpeedEl.textContent = newText;
        triggerGlow(stageSpeedEl);
      }
    }
    
    // 更新运行状态
    const stageStateEl = document.getElementById('status-stage-state');
    if (stageStateEl) {
      let statusText = '就绪';
      if (this.isRunning) {
        statusText = this.isPaused ? '已暂停' : '运行中';
      }
      const newText = `状态: ${statusText}`;
      if (stageStateEl.textContent !== newText) {
        stageStateEl.textContent = newText;
        triggerGlow(stageStateEl);
      }
    }
  }

  /**
   * 运行/暂停切换（合二为一）
   */
  private toggleRunPause(): void {
    if (!this.isRunning) {
      // 未运行：开始运行
      this.runCode();
    } else if (this.isPaused) {
      // 已暂停：恢复执行
      this.isPaused = false;
      this.updateRunButtonIcon();
      if (this.pauseResolve) {
        this.pauseResolve();
        this.pauseResolve = null;
      }
    } else {
      // 运行中：暂停
      this.isPaused = true;
      this.updateRunButtonIcon();
    }
  }

  /**
   * 暂停/恢复代码执行（已废弃，保留兼容）
   */
  private togglePause(): void {
    this.toggleRunPause();
  }

  /**
   * 复制代码到剪贴板
   */
  private copyCode(): void {
    if (!this.workspace) return;
    
    const code = Blockly.JavaScript?.workspaceToCode(this.workspace) || '';
    
    if (!code.trim()) {
      this.showToast('没有可复制的代码', 'warning');
      return;
    }
    
    navigator.clipboard.writeText(code).then(() => {
      this.showToast('代码已复制到剪贴板', 'success');
    }).catch(() => {
      this.showToast('复制失败', 'error');
    });
  }

  /**
   * 新建项目
   */
  private newProject(): void {
    // 清空工作区
    if (this.workspace) {
      this.workspace.clear();
    }
    
    // 重置角色
    this.sprites.clear();
    
    // 创建默认项目
    this.createDefaultProject();
    this.renderSpritesList();
    
    // 更新项目名称输入框
    const nameInput = document.getElementById('blocks-project-name') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = '我的项目';
    }
  }

  /**
   * 保存项目
   */
  private saveProject(): void {
    if (!this.currentProject) return;
    
    // 保存前同步项目名称
    const nameInput = document.getElementById('blocks-project-name') as HTMLInputElement;
    if (nameInput && nameInput.value) {
      this.currentProject.name = nameInput.value;
    }
    
    // 保存当前角色工作区
    this.saveCurrentSpriteWorkspace();
    
    // 更新项目数据
    this.currentProject.sprites = Array.from(this.sprites.values());
    this.currentProject.updatedAt = new Date().toISOString();
    
    // 获取已保存的项目列表
    const projects = this.loadProjects();
    
    // 更新或添加项目
    const existingIndex = projects.findIndex(p => p.id === this.currentProject!.id);
    if (existingIndex >= 0) {
      projects[existingIndex] = this.currentProject;
    } else {
      projects.push(this.currentProject);
    }
    
    // 保存到 localStorage
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    
    // 显示保存成功提示
    this.showToast(`项目「${this.currentProject.name}」已保存`, 'success');
    
    console.log('项目已保存:', this.currentProject.name);
  }
  
  /**
   * 显示 Toast 提示
   */
  private showToast(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration: number = 1500): void {
    const container = document.getElementById('toast-container');
    if (!container) return;
    
    // 创建 Toast 元素
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    
    // 图标映射
    const icons: Record<string, string> = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    
    toast.innerHTML = `
      <span class="toast-message">${message}</span>
    `;
    
    container.appendChild(toast);
    
    // 延迟添加 show 类以触发动画
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });
    
    // 自动消失
    setTimeout(() => {
      toast.classList.remove('show');
      toast.classList.add('hide');
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  /**
   * 加载项目列表
   */
  private loadProjects(): BlocksProject[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.PROJECTS);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * 显示项目列表弹窗
   */
  private showProjectsModal(): void {
    const modal = document.getElementById('blocks-projects-modal');
    if (!modal) return;
    
    // 渲染项目列表
    this.renderProjectsList();
    
    // 显示弹窗
    modal.classList.add('active');
    
    // 绑定关闭事件
    const closeBtn = document.getElementById('projects-modal-close');
    closeBtn?.addEventListener('click', () => {
      modal.classList.remove('active');
    });
    
    // 点击遮罩层关闭
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('active');
      }
    });
  }
  
  /**
   * 渲染项目列表
   */
  private renderProjectsList(): void {
    const listEl = document.getElementById('projects-list');
    const emptyEl = document.getElementById('projects-empty');
    if (!listEl || !emptyEl) return;
    
    const projects = this.loadProjects();
    
    if (projects.length === 0) {
      listEl.classList.add('hide');
      emptyEl.classList.add('show');
      return;
    }
    
    listEl.classList.remove('hide');
    emptyEl.classList.remove('show');
    
    // 按更新时间排序（最新的在前面）
    projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    
    listEl.innerHTML = projects.map(project => `
      <div class="project-item" data-id="${project.id}">
        <div class="project-item-icon">💾</div>
        <div class="project-item-info">
          <div class="project-item-name">${this.escapeHtml(project.name)}</div>
          <div class="project-item-time">${this.formatTime(project.updatedAt)}</div>
        </div>
        <div class="project-item-actions">
          <button class="project-item-btn load" title="加载项目" data-id="${project.id}">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
          <button class="project-item-btn delete" title="删除项目" data-id="${project.id}">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </div>
      </div>
    `).join('');
    
    // 绑定加载按钮事件
    listEl.querySelectorAll('.project-item-btn.load').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.loadProjectById(id);
      });
    });
    
    // 绑定删除按钮事件
    listEl.querySelectorAll('.project-item-btn.delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = (btn as HTMLElement).dataset.id;
        if (id) this.deleteProjectById(id);
      });
    });
    
    // 点击项目行也可以加载
    listEl.querySelectorAll('.project-item').forEach(item => {
      item.addEventListener('click', () => {
        const id = (item as HTMLElement).dataset.id;
        if (id) this.loadProjectById(id);
      });
    });
  }
  
  /**
   * 根据 ID 加载项目
   */
  private loadProjectById(id: string): void {
    const projects = this.loadProjects();
    const project = projects.find(p => p.id === id);
    if (!project) return;
    
    // 设置当前项目
    this.currentProject = project;
    
    // 清空并重建角色列表
    this.sprites.clear();
    project.sprites.forEach(sprite => {
      this.sprites.set(sprite.id, sprite);
    });
    
    // 选中第一个角色
    const firstSprite = project.sprites[0];
    if (firstSprite) {
      this.currentSprite = firstSprite;
      // 加载工作区
      if (this.workspace && firstSprite.workspace) {
        this.workspace.clear();
        const xml = Blockly.utils.xml.textToDom(firstSprite.workspace);
        Blockly.Xml.domToWorkspace(xml, this.workspace);
        
        // 延迟刷新工作区，解决积木块模糊问题
        requestAnimationFrame(() => {
          this.workspace?.resize();
          this.workspace?.scrollCenter();
        });
      }
    }
    
    // 更新 UI
    this.renderSpritesList();
    
    // 更新项目名称输入框
    const nameInput = document.getElementById('blocks-project-name') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = project.name;
    }
    
    // 关闭弹窗
    const modal = document.getElementById('blocks-projects-modal');
    modal?.classList.remove('active');
    
    console.log('项目已加载:', project.name);
  }
  
  /**
   * 根据 ID 删除项目
   */
  private deleteProjectById(id: string): void {
    const projects = this.loadProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) return;
    
    const projectName = projects[index].name;
    
    // 从列表中移除
    projects.splice(index, 1);
    
    // 保存到 localStorage
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    
    // 重新渲染列表
    this.renderProjectsList();
    
    console.log('项目已删除:', projectName);
  }
  
  /**
   * 格式化时间
   */
  private formatTime(isoString: string): string {
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 1) return '刚刚';
      if (diffMins < 60) return `${diffMins} 分钟前`;
      if (diffHours < 24) return `${diffHours} 小时前`;
      if (diffDays < 7) return `${diffDays} 天前`;
      
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    } catch {
      return '';
    }
  }

  /**
   * 加载设置
   */
  private loadSettings(): void {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (data) {
        this.settings = { ...this.settings, ...JSON.parse(data) };
      }
    } catch (e) {
      console.warn('加载设置失败');
    }
  }
  
  /**
   * 应用设置模块中的积木器配置
   */
  private applyBlocksSettings(): void {
    try {
      const data = localStorage.getItem('blocks_settings');
      if (data) {
        const blocksSettings = JSON.parse(data);
        
        // 应用默认舞台类型
        if (blocksSettings.defaultStageType) {
          this.stageType = blocksSettings.defaultStageType;
        }
        
        // 应用默认执行速度
        if (blocksSettings.executionSpeed) {
          const speedMap: Record<string, number> = {
            'slow': 2,
            'normal': 1,
            'fast': 0
          };
          this.executionSpeed = speedMap[blocksSettings.executionSpeed] ?? 0;
        }
        
        // 应用网格设置
        if (typeof blocksSettings.showGrid === 'boolean') {
          this.settings.gridEnabled = blocksSettings.showGrid;
        }
        
        // 应用尺子设置
        if (typeof blocksSettings.showRuler === 'boolean') {
          this.settings.rulerEnabled = blocksSettings.showRuler;
        }
        
        console.log('⚙️ 应用积木器设置:', blocksSettings);
      }
    } catch (e) {
      console.warn('加载积木器设置失败:', e);
    }
  }
  
  /**
   * 实时更新设置（从设置模块回调调用）
   */
  public updateSettings(settings: { showGrid?: boolean; showRuler?: boolean; executionSpeed?: string }): void {
    // 更新网格设置
    if (typeof settings.showGrid === 'boolean') {
      this.settings.gridEnabled = settings.showGrid;
    }
    
    // 更新尺子设置
    if (typeof settings.showRuler === 'boolean') {
      this.settings.rulerEnabled = settings.showRuler;
      // 更新尺子显示状态
      const rulerX = document.getElementById('canvas-ruler-x');
      const rulerY = document.getElementById('canvas-ruler-y');
      if (this.stageType !== 'text') {
        if (rulerX) rulerX.style.display = settings.showRuler ? 'block' : 'none';
        if (rulerY) rulerY.style.display = settings.showRuler ? 'block' : 'none';
      }
    }
    
    // 更新执行速度
    if (settings.executionSpeed) {
      const speedMap: Record<string, number> = {
        'slow': 2,
        'normal': 1,
        'fast': 0
      };
      this.executionSpeed = speedMap[settings.executionSpeed] ?? 0;
      this.updateSpeedButtonsUI();
    }
    
    // 重绘画布
    this.clearCanvas();
    console.log('⚙️ 实时更新积木器设置:', settings);
  }
  
  /**
   * 更新速度按钮UI状态
   */
  private updateSpeedButtonsUI(): void {
    document.querySelectorAll('.blocks-speed-control .speed-btn').forEach(btn => {
      const btnSpeed = parseInt((btn as HTMLElement).dataset.speed || '0');
      btn.classList.toggle('active', btnSpeed === this.executionSpeed);
    });
    this.updateStatusBar();
  }

  /**
   * 保存设置
   */
  private saveSettings(): void {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(this.settings));
  }
}

// 导出单例
export const blocksModule = new BlocksModule();
